// cspell:ignore Swipeable summarisation
import { Thing, ThingActivity } from '@eagle/core-data-types';
import {
  AddThingPersonDialog,
  BatchFaultWrapper,
  CacheDataTypes,
  CommonEntityWithDeleted,
  DISPLAY_ACTIVITIES_FEATURE_FLAG,
  EntityItemFields,
  EntityItems,
  EntityVisibility,
  evaluate,
  EventDialogProvider,
  EventHistory,
  EventHistoryAlert,
  EventHistoryMap,
  EventLocationData,
  filterTrustedActivities,
  getDateTimeFormat,
  getPortalLocale,
  HISTORY_MAP_CENTER,
  HistorySearchProvider,
  Icon,
  ListSearchProvider,
  MapStorageKeys,
  Maybe,
  MobileTabSelection,
  NotableEventsProvider,
  ShareableHistoryData,
  T_ONE,
  ThingPersonDialogOperation,
  ThingPersonDialogTabs,
  TRACK_THING_SUMMARY_STATISTICS_DEFAULT_TEMPLATE,
  TRACK_TRIP_SUMMARY_CARD_DEFAULT_TEMPLATE,
  TRACK_TRIP_TIMELINE_ITEM_DEFAULT_TEMPLATE,
  TRACK_TRIP_TIMELINE_MARKER_DEFAULT_TEMPLATE,
  TRUSTED_ACTIVITIES_RULES_FLAG,
  TrustedActivitiesRules,
  TrustedActivityRule,
  Undefinable,
  useAuthenticated,
  useBoolFlag,
  useCustomRoutes,
  useFetchAllCache,
  useFetchOneCache,
  useFlags,
  useNotableEvents,
  useNumberFlag,
  useObjectFlag,
  usePromise,
  useTitle,
  useUiTemplate
} from '@eagle/react-common';
import { Alert, Box, Button, Card, CardActions, CardContent, Collapse, DateTimePicker, Divider, Drawer, IconButton, ListItemIcon, ListItemText, LocalizationProvider, Menu, MenuItem, RecordSummaryCard, Skeleton, Stack, Switch, Typography } from '@ethings/ui-toolkit';
import { Theme, useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { FC, useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Contract, isNullable, isObject, isRecord, isString, maybeArray, maybeBoolean, maybeNumber } from 'validata';
import { EventHistoryNavigator } from '../event-history-navigator';
import { buildTripsCoreHistoryRange, clampRangeToMaxHours, HISTORY_CUSTOM_DATE_RANGE_FLAG, isRangeOverMaxHours, resolveTripHistoryMaxRangeHours, toMaxRangeDaysLabel, TRIPS_CORE_EXPERIENCE_FLAG } from '../history-date-range.utils';
import {
  HoveredEventPulseOverlay,
  ThingEventsSkeleton,
  ThingRangeMapState,
  ThingRangeMapViewportController,
  ThingRangeTimeline,
  ThingSummarySkeleton,
  TripDrawerContent,
  TripEventDrawerContent,
  TripHistoryMapViewportController,
  TripListSkeleton,
} from './components';
import {
  enrichEventsWithNearestLocationUpdate,
  filterExcludedTripHistoryEvents,
  getEventColor,
  getEventDisplayLabel,
  getTripTimelineProcessedEvents,
  isLocationUpdateEvent,
  toEventTime,
  type TripEventSummaryItem,
  type TripLocationEnrichmentOptions
} from './trip-history-base-view-utils';
import {
  formatTripGroupDayLabel,
  getPresetThingRangeBounds,
  getThingColorMapTyped,
  getTripBounds,
  getTripGroups,
  normalizeThingCustomRange,
  toThingRangeBounds
} from './trip-history-base-view.func';
import {
  ThingCustomRange,
  ThingRange,
  ThingRangeTimelineItem,
  TripBounds,
  TripDisplayMode,
  TripGeofenceSummary,
  TripGroup,
  TripHistoryEntity,
  TripMetricEvents,
  TripMetrics,
  TripSortOrder,
  TripSummary,
} from './trip-history-base-view.types';
import { fetchAllThingEvents } from './trip-history-paginated-events';
import { findContainingReferenceTrip, findReferenceEvent, isTripHistoryReferenceData, TripHistoryReferenceData } from './trip-history-reference-utils';
import { createThrottledReverseGeocodeResolver, formatAddressText, resolveTimelineAddressText } from './trip-history-route-address-utils';
import { useTripEventDetailsLoader } from './use-trip-event-details-loader';

export { TripEventDrawerContent } from './components/trip-event-drawer-content';

interface TripHistoryDesktopViewControllerProps {
  autoSelectFirstTrip?: boolean;
  mobileLayout?: boolean;
}

interface TripHistoryLocationState {
  dateEnd?: Date | string;
  dateStart?: Date | string;
  entityId?: string;
  entityItemFields?: {
    entity?: string;
  };
  lastLocationUpdateDate?: Date | string;
  referenceData?: unknown;
}

interface TripHistoryJourney {
  finish: string;
  start: string;
}

const toDateTimeFromUnknown = (value: unknown): Undefinable<DateTime> => {
  if (value instanceof Date) {
    const parsed = DateTime.fromJSDate(value);
    return parsed.isValid ? parsed : undefined;
  }

  if (typeof value === 'string') {
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed : undefined;
  }

  return undefined;
};

const decodeBase64Payload = (encodedData: string): string => {
  const normalized = encodedData
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
  return window.atob(`${normalized}${padding}`);
};

const toUrlSafeBase64 = (value: string): string => {
  return window
    .btoa(value)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

const toValidJourney = (journey: TripHistoryJourney): Undefinable<TripHistoryJourney> => {
  const start = DateTime.fromISO(journey.start);
  const finish = DateTime.fromISO(journey.finish);
  if (!start.isValid || !finish.isValid) return;

  const boundedStart = start.toMillis() <= finish.toMillis() ? start : finish;
  const boundedFinish = start.toMillis() <= finish.toMillis() ? finish : start;

  return {
    finish: boundedFinish.toISO() ?? boundedFinish.toJSDate().toISOString(),
    start: boundedStart.toISO() ?? boundedStart.toJSDate().toISOString(),
  };
};

const getMostRecentJourney = (journeys: TripHistoryJourney[]): Undefinable<TripHistoryJourney> => {
  const validJourneys = journeys
    .map((journey) => toValidJourney(journey))
    .filter((journey): journey is TripHistoryJourney => Boolean(journey));

  if (!validJourneys.length) return;

  return validJourneys.sort((first, second) => {
    const firstFinish = DateTime.fromISO(first.finish).toMillis();
    const secondFinish = DateTime.fromISO(second.finish).toMillis();
    if (firstFinish !== secondFinish) return secondFinish - firstFinish;

    const firstStart = DateTime.fromISO(first.start).toMillis();
    const secondStart = DateTime.fromISO(second.start).toMillis();
    return secondStart - firstStart;
  })[0];
};

const toLocalityLabel = (addressLabel: string): string => {
  const trimmed = addressLabel.trim();
  if (!trimmed) return trimmed;

  const parts = trimmed.split(',').map((part) => part.trim()).filter(Boolean);
  const localitySource = parts[1] ?? parts[0] ?? trimmed;
  const localityWithoutStatePostcode = localitySource.replace(/\s+[A-Z]{2,3}\s+\d{3,4}$/u, '').trim();

  return localityWithoutStatePostcode || trimmed;
};

const trustedRuleContract: Contract<TrustedActivityRule> = {
  include: maybeBoolean(),
  allowedProvisioning: isNullable(maybeArray(isString())),
  maxDurationMinutes: isNullable(maybeNumber()),
  minDurationSeconds: isNullable(maybeNumber()),
  ongoingMaxMinutes: isNullable(maybeNumber()),
};

const trustedRulesContract: Contract<TrustedActivitiesRules> = {
  activityTypes: isRecord(isObject(trustedRuleContract)),
  defaults: isObject(trustedRuleContract),
};

const trustedRulesValidator = isObject(trustedRulesContract);

export const TripHistoryDesktopViewController: FC<TripHistoryDesktopViewControllerProps> = ({
  autoSelectFirstTrip = true,
  mobileLayout = false,
}) => {
  const { data: routeData } = useParams<{ data?: string }>();
  const initialRouteDataRef = useRef(routeData);
  const location = useLocation();
  const navigate = useNavigate();
  const { thing, history } = useCustomRoutes();
  const { t } = useTranslation(['common', 'track']);
  const { enqueueSnackbar } = useSnackbar();
  const debouncedErrorToastRef = useRef(debounce(() => {
    enqueueSnackbar(t('track:page.history.history-data-not-found.label'), { variant: 'error' });
  }, 200));
  const debouncedErrorToast = debouncedErrorToastRef.current;
  const hydratedFromRouteDataRef = useRef(false);
  const hydratedFromLocationStateRef = useRef(false);
  const pendingPayloadSelectedTripIdRef = useRef<Maybe<string>>(null);
  const isInitialPayloadSelectionSettledRef = useRef(true);
  const skipNextSelectionResetRef = useRef(false);
  const theme = useTheme<Theme>();
  const thingColorPalette = useMemo<string[]>(() => {
    const colors = (theme as { history?: { colors?: string[] } }).history?.colors;
    if (colors?.length) return colors;
    return ['#1E75D8', '#00A389', '#F59E0B', '#8B5CF6', '#EF4444'];
  }, [theme]);
  const unknownLocationLabel = t('common:terms.unknown');
  const inProgressLabel = t('track:page.history.trip-base-view.labels.in-progress');
  const tripInProgressLabel = t('track:page.history.trip-base-view.labels.trip-in-progress');
  const singleRouteLabel = t('track:page.history.trip-base-view.labels.single-route');
  const historyTitle = t('common:common.labels.history');
  useTitle(historyTitle);
  const flags = useFlags() as unknown;
  const typedFlags = (typeof flags === 'object' && flags !== null) ? flags as Record<string, unknown> : {};
  const isTripsCoreExperienceEnabled = useBoolFlag(TRIPS_CORE_EXPERIENCE_FLAG);
  const displayActivitiesFeature = useBoolFlag(DISPLAY_ACTIVITIES_FEATURE_FLAG);
  const trustedActivitiesRules = useObjectFlag<TrustedActivitiesRules>(TRUSTED_ACTIVITIES_RULES_FLAG, trustedRulesValidator);
  const trustedRules = useMemo(() => {
    return displayActivitiesFeature && trustedActivitiesRules ? trustedActivitiesRules : null;
  }, [displayActivitiesFeature, trustedActivitiesRules]);
  const maxHistoryCustomDateRangeHours = resolveTripHistoryMaxRangeHours(useNumberFlag(HISTORY_CUSTOM_DATE_RANGE_FLAG));
  const isTripsExperienceEnabled = typedFlags['track-history-trips-experience-trips-enabled-temporary'] !== false;
  const reverseGeocodeEnabled = typedFlags['track-trip-experience-reverse-geocode'] === true;
  const [isDrawerOpen] = useState(true);
  const [, setIsTransitioning] = useState(false);
  const [sortOrder, setSortOrder] = useState<TripSortOrder>('latest-first');
  const [tripHistoryData, setTripHistoryData] = useState<Undefinable<ShareableHistoryData>>();
  const [pendingReferenceData, setPendingReferenceData] = useState<Maybe<TripHistoryReferenceData>>(null);
  const [thingColorById, setThingColorById] = useState<Record<string, string>>({});
  const [thingDisplayById, setThingDisplayById] = useState<Record<string, string>>({});
  const [thingRangesById, setThingRangesById] = useState<Record<string, ThingRange>>({});
  const [thingCustomRangesById, setThingCustomRangesById] = useState<Record<string, ThingCustomRange>>({});
  const [thingCustomRangeDraftsById, setThingCustomRangeDraftsById] = useState<Record<string, ThingCustomRange>>({});
  const [thingCustomRangeEditorOpenById, setThingCustomRangeEditorOpenById] = useState<Record<string, boolean>>({});
  const [thingPreviousPresetRangeById, setThingPreviousPresetRangeById] = useState<Record<string, Exclude<ThingRange, 'custom'>>>({});
  const [hiddenThingIds, setHiddenThingIds] = useState<Record<string, boolean>>({});
  const [removedThingIds, setRemovedThingIds] = useState<Record<string, boolean>>({});
  const [isAddEntityDialogOpen, setIsAddEntityDialogOpen] = useState(false);
  const [thingMenuAnchor, setThingMenuAnchor] = useState<Maybe<HTMLElement>>(null);
  const [thingMenuEntityId, setThingMenuEntityId] = useState<Maybe<string>>(null);
  const [dateRangeMenuAnchor, setDateRangeMenuAnchor] = useState<Maybe<HTMLElement>>(null);
  const [dateRangeMenuEntityId, setDateRangeMenuEntityId] = useState<Maybe<string>>(null);
  const [viewModeMenuAnchor, setViewModeMenuAnchor] = useState<Maybe<HTMLElement>>(null);
  const [sortOrderMenuAnchor, setSortOrderMenuAnchor] = useState<Maybe<HTMLElement>>(null);
  const [tripDisplayMode, setTripDisplayMode] = useState<TripDisplayMode>(isTripsExperienceEnabled ? 'trips' : 'events');
  const [thingHeaderHoveredById, setThingHeaderHoveredById] = useState<Record<string, boolean>>({});
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Record<string, boolean>>({});
  const [collapsedThingIds, setCollapsedThingIds] = useState<Record<string, boolean>>({});
  const [hoveredEventId, setHoveredEventId] = useState<Maybe<string>>(null);
  const [timelineHoveredEventId, setTimelineHoveredEventId] = useState<Maybe<string>>(null);
  const [selectedEvent, setSelectedEvent] = useState<Maybe<EventLocationData>>(null);
  const [selectedTripEventId, setSelectedTripEventId] = useState<Maybe<string>>(null);
  const [selectedTripId, setSelectedTripId] = useState<Maybe<string>>(null);
  const [selectedThingId, setSelectedThingId] = useState<Maybe<string>>(null);
  const [thingRangeFocusEvent, setThingRangeFocusEvent] = useState<Maybe<EventLocationData>>(null);
  const [showThingLocationUpdates, setShowThingLocationUpdates] = useState(false);
  const [tripGeofenceSummaryByTripId, setTripGeofenceSummaryByTripId] = useState<Record<string, TripGeofenceSummary>>({});
  const [tripEventSummaryByTripId, setTripEventSummaryByTripId] = useState<Record<string, TripEventSummaryItem[]>>({});
  const [tripMetricsByTripId, setTripMetricsByTripId] = useState<Record<string, TripMetrics>>({});
  const [tripMetricEventsByTripId, setTripMetricEventsByTripId] = useState<Record<string, TripMetricEvents>>({});
  const [tripRouteFullAddressLabelByTripId, setTripRouteFullAddressLabelByTripId] = useState<Record<string, string>>({});
  const [tripRouteLabelByTripId, setTripRouteLabelByTripId] = useState<Record<string, string>>({});
  const [tripEventDetailsLoadingByTripId, setTripEventDetailsLoadingByTripId] = useState<Record<string, boolean>>({});
  const [thingRangeSuburbLabelByEventId, setThingRangeSuburbLabelByEventId] = useState<Record<string, string>>({});
  const [thingRangeSuburbLabelLoadingByEventId, setThingRangeSuburbLabelLoadingByEventId] = useState<Record<string, boolean>>({});
  const [visibleThingRangeTimelineItemIds, setVisibleThingRangeTimelineItemIds] = useState<string[]>([]);
  const { restClient } = useAuthenticated();
  const thingCache = useFetchOneCache(CacheDataTypes.THING);
  const geofenceCache = useFetchOneCache(CacheDataTypes.GEOFENCE);
  const geofenceTypeCache = useFetchAllCache(CacheDataTypes.GEOFENCE_TYPE);
  const { template: thingRangeTimelineItemTemplate, loaded: thingRangeTimelineItemTemplateLoaded } = useUiTemplate(
    'track-trip-timeline-item',
    TRACK_TRIP_TIMELINE_ITEM_DEFAULT_TEMPLATE,
  );
  const { template: thingRangeTimelineMarkerTemplate, loaded: thingRangeTimelineMarkerTemplateLoaded } = useUiTemplate(
    'track-trip-timeline-marker',
    TRACK_TRIP_TIMELINE_MARKER_DEFAULT_TEMPLATE,
  );
  const { template: tripSummaryCardTemplate, loaded: tripSummaryCardTemplateLoaded } = useUiTemplate(
    'track-trip-summary-card',
    TRACK_TRIP_SUMMARY_CARD_DEFAULT_TEMPLATE,
  );
  const { template: thingSummaryStatisticsTemplate, loaded: thingSummaryStatisticsTemplateLoaded } = useUiTemplate(
    'track-thing-summary-statistics',
    TRACK_THING_SUMMARY_STATISTICS_DEFAULT_TEMPLATE,
  );
  useEffect(() => {
    if (!isTripsExperienceEnabled) {
      setTripDisplayMode('events');
    }
  }, [isTripsExperienceEnabled]);
  const eventSummarizationFeature = Boolean(typedFlags['track-thing-history-event-summarisation-feature']);
  const rawEventSummarizationTemplate: unknown = typedFlags['track-thing-history-event-summarisation-template'];
  const rawLocationThresholdMinutes = typedFlags['track-history-trips-core-experience-location-threshold'];
  const reverseGeocodeLabelByCoordinateRef = useRef<Map<string, string>>(new Map());
  const reverseGeocodeInFlightByCoordinateRef = useRef<Map<string, Promise<string>>>(new Map());

  useEffect(() => {
    if (hydratedFromRouteDataRef.current) return;

    const encodedData = initialRouteDataRef.current;
    if (!encodedData) return;

    let isCancelled = false;

    const hydrateFromRouteData = async (): Promise<void> => {
      try {
        const decodedData = JSON.parse(decodeBase64Payload(encodedData)) as ShareableHistoryData;
        if (!decodedData?.entities?.length) throw new Error('Missing entities');

        const entityCandidates = decodedData.entities.filter((entity) => {
          return entity?.type === CacheDataTypes.THING && typeof entity._id === 'string';
        });
        if (!entityCandidates.length) throw new Error('Missing supported entities');

        const hydratedEntities = await Promise.all(entityCandidates.map(async (entity) => {
          const thingEntity = await thingCache.one<Thing>(entity._id);
          if (!thingEntity || thingEntity.deleted) return;

          const mostRecentJourney = getMostRecentJourney((entity.journeys ?? []) as TripHistoryJourney[]);
          if (!mostRecentJourney) return;

          return {
            ...entity,
            journeys: [mostRecentJourney],
          };
        }));

        if (isCancelled) return;

        const validEntities = hydratedEntities.filter((entity): entity is TripHistoryEntity => Boolean(entity));
        if (!validEntities.length || validEntities.length !== entityCandidates.length) {
          throw new Error('Invalid entities');
        }

        const hydratedData: ShareableHistoryData = {
          ...decodedData,
          entities: validEntities,
        };

        const thingRanges = validEntities.reduce<Record<string, ThingCustomRange>>((current, entity) => {
          const journey = entity.journeys[0];
          current[entity._id] = {
            end: DateTime.fromISO(journey.finish),
            start: DateTime.fromISO(journey.start),
          };
          return current;
        }, {});

        setTripHistoryData(hydratedData);
        setThingColorById((current) => (
          getThingColorMapTyped as <T extends { _id: string }>(
            entities: T[],
            currentColorById?: Record<string, string>,
            thingColors?: string[],
          ) => Record<string, string>
        )(hydratedData.entities, current, thingColorPalette));
        setThingDisplayById((current) => {
          return hydratedData.entities.reduce<Record<string, string>>((next, entity) => {
            next[entity._id] = current[entity._id] ?? entity._id;
            return next;
          }, { ...current });
        });
        setThingRangesById((current) => {
          return hydratedData.entities.reduce<Record<string, ThingRange>>((next, entity) => {
            next[entity._id] = 'custom';
            return next;
          }, { ...current });
        });
        setThingCustomRangesById((current) => ({
          ...current,
          ...thingRanges,
        }));
        setThingCustomRangeEditorOpenById((current) => {
          return hydratedData.entities.reduce<Record<string, boolean>>((next, entity) => {
            next[entity._id] = false;
            return next;
          }, { ...current });
        });
        setThingPreviousPresetRangeById((current) => {
          return hydratedData.entities.reduce<Record<string, Exclude<ThingRange, 'custom'>>>((next, entity) => {
            next[entity._id] = 'this-week';
            return next;
          }, { ...current });
        });

        setSelectedThingId(validEntities[0]?._id ?? null);
        setSelectedTripId(hydratedData.selectedTripId ?? null);
        pendingPayloadSelectedTripIdRef.current = hydratedData.selectedTripId ?? null;
        isInitialPayloadSelectionSettledRef.current = !hydratedData.selectedTripId;
        setPendingReferenceData(hydratedData.eventId ? { type: 'event', typeId: hydratedData.eventId } : null);

        const payloadTripDisplayMode = hydratedData.tripDisplayMode === 'events' || hydratedData.tripDisplayMode === 'trips'
          ? hydratedData.tripDisplayMode
          : undefined;
        setTripDisplayMode(isTripsExperienceEnabled ? (payloadTripDisplayMode ?? 'trips') : 'events');

        hydratedFromLocationStateRef.current = true;
      } catch (error) {
        if (debouncedErrorToast) debouncedErrorToast();
      } finally {
        hydratedFromRouteDataRef.current = true;
      }
    };

    void hydrateFromRouteData();

    return () => {
      isCancelled = true;
    };
  }, [debouncedErrorToast, isTripsExperienceEnabled, thingCache, thingColorPalette]);

  useEffect(() => {
    if (tripHistoryData?.entities?.length) return;
    if (hydratedFromLocationStateRef.current) return;

    const state = (location.state ?? undefined) as Undefinable<TripHistoryLocationState>;
    if (!state?.entityId || !state?.entityItemFields?.entity) return;

    const startFromState = toDateTimeFromUnknown(state.dateStart);
    const endFromState = toDateTimeFromUnknown(state.dateEnd);
    const fromLastLocation = toDateTimeFromUnknown(state.lastLocationUpdateDate);

    const hasStateRange = Boolean(startFromState && endFromState);
    const stateRange = hasStateRange
      ? (isTripsCoreExperienceEnabled
        ? clampRangeToMaxHours(startFromState as DateTime, endFromState as DateTime, maxHistoryCustomDateRangeHours)
        : { end: endFromState as DateTime, start: startFromState as DateTime })
      : undefined;
    const tripsCoreFallbackRange = isTripsCoreExperienceEnabled && fromLastLocation
      ? buildTripsCoreHistoryRange(fromLastLocation, maxHistoryCustomDateRangeHours)
      : undefined;

    const computedStart = stateRange
      ? stateRange.start
      : (fromLastLocation
        ? (isTripsCoreExperienceEnabled
          ? toDateTimeFromUnknown(tripsCoreFallbackRange?.dateStart)
          : fromLastLocation.startOf('day'))
        : undefined);
    const computedEnd = stateRange
      ? stateRange.end
      : (fromLastLocation
        ? (isTripsCoreExperienceEnabled
          ? toDateTimeFromUnknown(tripsCoreFallbackRange?.dateEnd)
          : fromLastLocation.plus({ day: 1 }).startOf('day'))
        : undefined);

    if (!computedStart || !computedEnd) return;

    const start = computedStart.toMillis() <= computedEnd.toMillis() ? computedStart : computedEnd;
    const end = computedStart.toMillis() <= computedEnd.toMillis() ? computedEnd : computedStart;
    const entityId = state.entityId;

    const fallbackData: ShareableHistoryData = {
      entities: [
        {
          _id: entityId,
          journeys: [
            {
              finish: end.toISO() ?? end.toJSDate().toISOString(),
              start: start.toISO() ?? start.toJSDate().toISOString(),
            },
          ],
          type: state.entityItemFields.entity,
        },
      ],
      tripDisplayMode: isTripsExperienceEnabled ? 'trips' : 'events',
    };

    setTripHistoryData(fallbackData);
    setThingColorById((current) => (
      getThingColorMapTyped as <T extends { _id: string }>(
        entities: T[],
        currentColorById?: Record<string, string>,
        thingColors?: string[],
      ) => Record<string, string>
    )(fallbackData.entities, current, thingColorPalette));
    setThingDisplayById((current) => ({
      ...current,
      [entityId]: current[entityId] ?? entityId,
    }));
    setThingRangesById((current) => ({
      ...current,
      [entityId]: 'custom',
    }));
    setThingCustomRangesById((current) => ({
      ...current,
      [entityId]: { end, start },
    }));
    setThingCustomRangeEditorOpenById((current) => ({
      ...current,
      [entityId]: false,
    }));
    setThingPreviousPresetRangeById((current) => ({
      ...current,
      [entityId]: 'this-week',
    }));
    setSelectedThingId(entityId);
    setSelectedTripId(null);
    setPendingReferenceData(isTripHistoryReferenceData(state.referenceData) ? state.referenceData : null);
    hydratedFromLocationStateRef.current = true;
  }, [isTripsCoreExperienceEnabled, isTripsExperienceEnabled, location.state, maxHistoryCustomDateRangeHours, thingColorPalette, tripHistoryData]);

  const filteredEntities = useMemo(() => {
    if (!tripHistoryData) return [];

    return tripHistoryData.entities
      .filter((entity) => !removedThingIds[entity._id])
      .filter((entity) => !(entity._id === 'Unknown thing' && entity.journeys.length === 0))
      .map((entity) => {
        const range: ThingRange = thingRangesById[entity._id] ?? (thingCustomRangesById[entity._id] ? 'custom' : 'this-week');
        const customRange: Undefinable<ThingCustomRange> = thingCustomRangesById[entity._id];
        const rangeBounds = toThingRangeBounds(range, customRange, DateTime.now());

        if (!rangeBounds) {
          return {
            ...entity,
            journeys: [],
          };
        }

        if (entity.type === CacheDataTypes.THING) {
          return {
            ...entity,
            journeys: [{
              finish: rangeBounds.end.toISO() ?? rangeBounds.end.toJSDate().toISOString(),
              start: rangeBounds.start.toISO() ?? rangeBounds.start.toJSDate().toISOString(),
            }],
          };
        }

        const journeys = entity.journeys.flatMap((journey) => {
          const journeyStart = DateTime.fromISO(journey.start);
          const journeyFinish = DateTime.fromISO(journey.finish);
          if (!journeyStart.isValid || !journeyFinish.isValid) return [];
          if (journeyStart > rangeBounds.end || journeyFinish < rangeBounds.start) return [];

          const boundedStart = DateTime.fromMillis(Math.max(journeyStart.toMillis(), rangeBounds.start.toMillis()));
          const boundedFinish = DateTime.fromMillis(Math.min(journeyFinish.toMillis(), rangeBounds.end.toMillis()));

          return [{
            ...journey,
            finish: boundedFinish.toISO() ?? journey.finish,
            start: boundedStart.toISO() ?? journey.start,
          }];
        });

        return { ...entity, journeys };
      });
  }, [removedThingIds, tripHistoryData, thingCustomRangesById, thingRangesById]);

  const defaultEntityItems = useMemo<Undefinable<EntityItems[]>>(() => {
    if (!filteredEntities.length) return;

    let colorIndex = -1;

    return filteredEntities.map((item) => {
      const entityType = CacheDataTypes.THING_TYPE === `${item.type}-type` ? CacheDataTypes.THING_TYPE : CacheDataTypes.PERSON_TYPE;
      return {
        entity: CacheDataTypes.THING === item.type ? CacheDataTypes.THING : CacheDataTypes.PERSON,
        entityType,
        entityTypeKey: CacheDataTypes.THING === item.type ? 'thingTypeId' : 'personTypeId',
        id: item._id,
        journeyList: item.journeys.map((journey, index) => {
          colorIndex++;
          return {
            colorIndex,
            dateRange: {
              endTime: DateTime.fromISO(journey.finish).toJSDate(),
              startTime: DateTime.fromISO(journey.start).toJSDate(),
            },
            entityId: item._id,
            entityType,
            focused: true,
            id: `${item._id}-${index}`,
          };
        }),
      };
    });
  }, [filteredEntities]);

  const defaultVisibilities = useMemo(() => {
    return defaultEntityItems?.map((defaultEntityItem) => {
      const defaultVisibilityItem: EntityVisibility = {
        entityId: defaultEntityItem.id,
        isVisible: !hiddenThingIds[defaultEntityItem.id],
        journeyVisibility: defaultEntityItem.journeyList.map((journey) => ({
          isVisible: !hiddenThingIds[defaultEntityItem.id],
          journeyId: journey.id,
        })),
      };
      return defaultVisibilityItem;
    });
  }, [defaultEntityItems, hiddenThingIds]);

  const defaultSelectedEventId = useMemo(() => {
    if (!tripHistoryData) return;
    return tripHistoryData.eventId;
  }, [tripHistoryData]);

  const payloadTripSummaries = useMemo<TripSummary[]>(() => {
    if (!filteredEntities.length) return [];

    const summaries = filteredEntities.flatMap((entity) => {
      return entity.journeys.map((journey, index) => {
        const start = DateTime.fromISO(journey.start);
        const finish = DateTime.fromISO(journey.finish);
        const journeyId = `${entity._id}-${index}`;

        return {
          entityId: entity._id,
          finish,
          id: journeyId,
          isOngoing: false,
          journeyId,
          start,
        };
      });
    });

    return summaries;
  }, [filteredEntities]);

  const [tripSummaries, , tripSummariesState] = usePromise<TripSummary[]>(
    async () => {
      if (!filteredEntities.length) return [];

      const summariesByEntity = await Promise.all(filteredEntities.map(async (entity) => {
        if (entity.type !== CacheDataTypes.THING || !entity.journeys.length) return [];

        const journeyRanges = entity.journeys.map((journey, index) => ({
          end: DateTime.fromISO(journey.finish),
          id: `${entity._id}-${index}`,
          start: DateTime.fromISO(journey.start),
        }));

        const earliestStart = journeyRanges.reduce((current, range) => {
          return range.start.toMillis() < current.toMillis() ? range.start : current;
        }, journeyRanges[0].start);

        const latestEnd = journeyRanges.reduce((current, range) => {
          return range.end.toMillis() > current.toMillis() ? range.end : current;
        }, journeyRanges[0].end);

        const activities = await restClient.thingActivity.fetchAllByThing(
          entity._id,
          earliestStart.toJSDate(),
          latestEnd.toJSDate(),
        );
        const thingActivities = activities.filter((activity): activity is ThingActivity => Boolean(activity?.start));
        const trustedActivities = filterTrustedActivities(thingActivities, trustedRules);

        return trustedActivities
          .filter((activity): activity is ThingActivity => {
            if (!activity?.start || activity.activityType !== 'trip') return false;

            const activityStart = DateTime.fromJSDate(activity.start);
            const activityFinish = activity.finish ? DateTime.fromJSDate(activity.finish) : DateTime.now();

            return journeyRanges.some((range) => (
              activityStart <= range.end
              && activityFinish >= range.start
            ));
          })
          .map((activity) => {
            const start = DateTime.fromJSDate(activity.start);
            const finish = activity.finish ? DateTime.fromJSDate(activity.finish) : DateTime.now();
            const matchingRange = journeyRanges.find((range) => (
              start <= range.end
              && finish >= range.start
            )) ?? journeyRanges[0];
            const activityIdentifier = activity.startThingActivityEventId
              || activity.finishThingActivityEventId
              || start.toMillis().toString();

            return {
              entityId: entity._id,
              finish,
              id: `${matchingRange.id}:${activityIdentifier}`,
              isOngoing: !activity.finish,
              journeyId: matchingRange.id,
              start,
            };
          });
      }));

      const summaries = summariesByEntity.flat();

      return summaries.sort((first, second) => {
        if (sortOrder === 'oldest-first') return first.start.toMillis() - second.start.toMillis();
        return second.start.toMillis() - first.start.toMillis();
      });
    },
    [filteredEntities, restClient.thingActivity, sortOrder, trustedRules],
  );
  const resolvedTripSummaries = useMemo(() => tripSummaries ?? [], [tripSummaries]);

  useEffect(() => {
    if (!tripHistoryData) return;

    if (!filteredEntities.length) {
      const historyPath = `/${history}`;
      if (location.pathname !== historyPath) {
        navigate(historyPath, { replace: true });
      }
      return;
    }

    if (!isInitialPayloadSelectionSettledRef.current && tripDisplayMode === 'trips') return;

    const encodedData: ShareableHistoryData = {
      entities: filteredEntities,
      eventId: selectedEvent?.eventId || undefined,
      selectedTripId: selectedTripId || undefined,
      tripDisplayMode,
    };

    const encodedStr = toUrlSafeBase64(JSON.stringify(encodedData));
    if (routeData === encodedStr) return;

    navigate(`/${history}/${encodedStr}`, { replace: true });
  }, [
    filteredEntities,
    history,
    location.pathname,
    navigate,
    routeData,
    selectedEvent?.eventId,
    selectedTripId,
    tripDisplayMode,
    tripHistoryData,
  ]);

  const eventDefinitions = useNotableEvents();
  const eventDefinitionsForSummarization = useMemo<unknown[]>(() => {
    return Array.isArray(eventDefinitions)
      ? eventDefinitions.filter((eventDefinition) => eventDefinition.enabled && eventDefinition.notableEvent)
      : [];
  }, [eventDefinitions]);
  const tripEventSummarizationOptions = useMemo(() => ({
    enabled: Boolean(eventSummarizationFeature),
    notableEvents: eventDefinitionsForSummarization,
    template: rawEventSummarizationTemplate && typeof rawEventSummarizationTemplate === 'object'
      ? rawEventSummarizationTemplate
      : undefined,
  }), [eventDefinitionsForSummarization, eventSummarizationFeature, rawEventSummarizationTemplate]);
  const tripLocationEnrichmentOptions = useMemo<TripLocationEnrichmentOptions | undefined>(() => {
    const thresholdCandidate = typeof rawLocationThresholdMinutes === 'number'
      ? rawLocationThresholdMinutes
      : Number(rawLocationThresholdMinutes);
    if (!Number.isFinite(thresholdCandidate) || thresholdCandidate <= 0) return undefined;
    return { thresholdMinutes: thresholdCandidate };
  }, [rawLocationThresholdMinutes]);

  const getThingEvents = useCallback((entityId: string, params: Record<string, unknown>) => {
    return fetchAllThingEvents<unknown>(
      (thingId, requestParams) => restClient.thingEvent.getWithThing(thingId, requestParams as never),
      entityId,
      params,
    );
  }, [restClient.thingEvent]);

  const getLastThingEvent = useCallback((entityId: string, params: Record<string, unknown>) => {
    return restClient.lastThingEvent.getWithThing(entityId, params as never);
  }, [restClient.lastThingEvent]);

  useTripEventDetailsLoader({
    eventDefinitions,
    geofenceCache,
    geofenceTypeCache,
    getLastThingEvent,
    inProgressLabel,
    resolvedTripSummaries,
    reverseGeocodeApi: restClient.reverseGeocode,
    setTripEventDetailsLoadingByTripId,
    setTripEventSummaryByTripId,
    setTripGeofenceSummaryByTripId,
    setTripMetricsByTripId,
    setTripMetricEventsByTripId,
    setTripRouteFullAddressLabelByTripId,
    setTripRouteLabelByTripId,
    singleRouteLabel,
    getThingEvents,
    tripLocationEnrichmentOptions,
    tripEventSummarizationOptions,
    tripInProgressLabel,
    unknownLocationLabel,
  });

  useEffect(() => {
    if (tripDisplayMode === 'events') {
      isInitialPayloadSelectionSettledRef.current = true;
      pendingPayloadSelectedTripIdRef.current = null;
      setSelectedTripId(null);
      return;
    }

    if (!resolvedTripSummaries.length) {
      if (!isInitialPayloadSelectionSettledRef.current) return;
      setSelectedTripId(null);
      return;
    }

    if (!isInitialPayloadSelectionSettledRef.current) {
      const payloadSelectedTripId = pendingPayloadSelectedTripIdRef.current;
      pendingPayloadSelectedTripIdRef.current = null;
      isInitialPayloadSelectionSettledRef.current = true;

      if (payloadSelectedTripId && resolvedTripSummaries.some((trip) => trip.id === payloadSelectedTripId)) {
        setSelectedTripId(payloadSelectedTripId);
        return;
      }
    }

    if (!autoSelectFirstTrip) {
      setSelectedTripId((currentTripId) => {
        if (!currentTripId) return null;
        const currentTrip = resolvedTripSummaries.find((trip) => trip.id === currentTripId);
        if (currentTrip && (!selectedThingId || currentTrip.entityId === selectedThingId)) return currentTripId;
        return null;
      });
      return;
    }

    setSelectedTripId((currentTripId) => {
      const currentTrip = resolvedTripSummaries.find((trip) => trip.id === currentTripId);
      if (currentTrip && (!selectedThingId || currentTrip.entityId === selectedThingId)) return currentTripId;
      return resolvedTripSummaries.find((trip) => trip.entityId === selectedThingId)?.id ?? resolvedTripSummaries[0].id;
    });
  }, [autoSelectFirstTrip, resolvedTripSummaries, selectedThingId, tripDisplayMode]);

  const selectedTrip = useMemo(() => {
    if (!selectedTripId) return;
    return resolvedTripSummaries.find((trip) => trip.id === selectedTripId);
  }, [resolvedTripSummaries, selectedTripId]);

  useEffect(() => {
    if (!selectedTrip) return;
    setSelectedThingId(selectedTrip.entityId);
  }, [selectedTrip]);

  const selectedTripJourneyId = selectedTrip?.journeyId;
  const visibleJourneyIds = useMemo(() => selectedTripJourneyId ? [selectedTripJourneyId] : [], [selectedTripJourneyId]);

  useEffect(() => {
    if (skipNextSelectionResetRef.current) {
      skipNextSelectionResetRef.current = false;
      return;
    }
    setSelectedEvent(null);
    setSelectedTripEventId(null);
    setThingRangeFocusEvent(null);
  }, [selectedTripId]);

  useEffect(() => {
    setSelectedEvent(null);
    setSelectedTripEventId(null);
    setHoveredEventId(null);
    setTimelineHoveredEventId(null);
    setThingRangeFocusEvent(null);
  }, [tripDisplayMode]);

  const providerKey = useMemo(() => {
    return JSON.stringify({
      entities: filteredEntities.map((entity) => ({
        id: entity._id,
        journeys: entity.journeys.map((journey) => ({
          finish: journey.finish,
          start: journey.start,
        })),
      })),
      hidden: hiddenThingIds,
      removed: removedThingIds,
      ranges: thingRangesById,
    });
  }, [filteredEntities, hiddenThingIds, removedThingIds, thingRangesById]);

  const selectedEntityId = useMemo(() => {
    if (selectedTrip?.entityId) return selectedTrip.entityId;
    if (selectedThingId && filteredEntities.some((entity) => entity._id === selectedThingId)) return selectedThingId;
    if (!filteredEntities.length) return 'Unknown thing';
    return filteredEntities[0]._id;
  }, [filteredEntities, selectedTrip, selectedThingId]);

  useEffect(() => {
    if (!filteredEntities.length) {
      setSelectedThingId(null);
      return;
    }

    setSelectedThingId((current) => {
      if (current && filteredEntities.some((entity) => entity._id === current)) return current;
      return filteredEntities[0]._id;
    });
  }, [filteredEntities]);

  const selectedThingColor = useMemo(() => {
    return thingColorById[selectedEntityId] ?? thingColorPalette[0];
  }, [selectedEntityId, thingColorById, thingColorPalette]);
  const isSelectedThingHidden = hiddenThingIds[selectedEntityId] === true;

  const selectedThingDateRange = useMemo<Undefinable<TripBounds>>(() => {
    const selectedThingRange: ThingRange = thingRangesById[selectedEntityId] ?? (thingCustomRangesById[selectedEntityId] ? 'custom' : 'this-week');
    const selectedThingCustomRange: Undefinable<ThingCustomRange> = thingCustomRangesById[selectedEntityId];
    return toThingRangeBounds(selectedThingRange, selectedThingCustomRange, DateTime.now());
  }, [selectedEntityId, thingCustomRangesById, thingRangesById]);

  const thingRangeJourneyId = useMemo(() => {
    if (!selectedThingDateRange) return `${selectedEntityId}-events`;
    return `${selectedEntityId}-events-${selectedThingDateRange.start.toMillis()}-${selectedThingDateRange.end.toMillis()}`;
  }, [selectedEntityId, selectedThingDateRange]);

  const mapVisibleJourneyIds = useMemo(() => {
    if (tripDisplayMode === 'events' && isSelectedThingHidden) return [];
    if (tripDisplayMode === 'events') return [thingRangeJourneyId];
    return visibleJourneyIds;
  }, [isSelectedThingHidden, tripDisplayMode, thingRangeJourneyId, visibleJourneyIds]);

  const selectedThingDateRangeStartMillis = selectedThingDateRange?.start.toMillis() ?? null;
  const selectedThingDateRangeEndMillis = selectedThingDateRange?.end.toMillis() ?? null;
  const shouldLoadThingRangeEvents = (tripDisplayMode === 'events' || Boolean(pendingReferenceData))
    && selectedThingDateRangeStartMillis !== null
    && selectedThingDateRangeEndMillis !== null
    && selectedEntityId !== 'Unknown thing';

  const { data: thingRangeEvents, status: thingRangeEventsState } = useQuery<EventLocationData[]>({
    enabled: shouldLoadThingRangeEvents,
    gcTime: 1000 * 60 * 30,
    queryFn: async () => {
      if (
        selectedThingDateRangeStartMillis === null
        || selectedThingDateRangeEndMillis === null
        || selectedEntityId === 'Unknown thing'
      ) {
        return [];
      }

      const rangeStart = DateTime.fromMillis(selectedThingDateRangeStartMillis);
      const rangeEnd = DateTime.fromMillis(selectedThingDateRangeEndMillis);

      const response = await fetchAllThingEvents<EventLocationData>(
        (thingId, requestParams) => restClient.thingEvent.getWithThing(thingId, requestParams as never),
        selectedEntityId,
        {
          dateRangeFinish: rangeEnd.toJSDate(),
          dateRangeStart: rangeStart.toJSDate(),
          sort: 'occurred',
        },
      );

      return response.items
        .filter((event) => {
          const eventTime = toEventTime(event.occurred);
          return eventTime >= rangeStart && eventTime <= rangeEnd;
        })
        .sort((first, second) => toEventTime(first.occurred).toMillis() - toEventTime(second.occurred).toMillis());
    },
    queryKey: [
      'trip-history-thing-range-events',
      selectedEntityId,
      tripDisplayMode,
      pendingReferenceData?.type ?? null,
      pendingReferenceData?.typeId ?? null,
      pendingReferenceData?.type === 'alert'
        ? (pendingReferenceData.occurred instanceof Date ? pendingReferenceData.occurred.toISOString() : pendingReferenceData.occurred)
        : null,
      selectedThingDateRangeStartMillis,
      selectedThingDateRangeEndMillis,
    ],
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 3,
  });

  const resolvedThingRangeEvents = useMemo(() => {
    return enrichEventsWithNearestLocationUpdate(thingRangeEvents ?? [], tripLocationEnrichmentOptions);
  }, [thingRangeEvents, tripLocationEnrichmentOptions]);
  const reverseGeocodeApi = useMemo(() => (
    restClient.reverseGeocode as unknown as {
      get: (params: { lat: number; long: number }) => Promise<{ address?: unknown }>;
    }
  ), [restClient.reverseGeocode]);
  const throttledReverseGeocode = useMemo(() => {
    return createThrottledReverseGeocodeResolver((params) => reverseGeocodeApi.get(params), { intervalMs: 250 });
  }, [reverseGeocodeApi]);
  const resolvedThingRangeSuburbLabelByEventId = useMemo<Record<string, string>>(
    () => thingRangeSuburbLabelByEventId ?? {},
    [thingRangeSuburbLabelByEventId],
  );

  const selectTripEvent = useCallback((event: EventLocationData): void => {
    setSelectedTripEventId(event.eventId);
    setSelectedEvent(event);
    setThingRangeFocusEvent(event);
  }, []);

  useEffect(() => {
    if (!pendingReferenceData) return;
    if (!isTripsCoreExperienceEnabled) {
      setPendingReferenceData(null);
      return;
    }
    if (thingRangeEventsState === 'pending') return;

    const matchingEvent = findReferenceEvent(resolvedThingRangeEvents, pendingReferenceData);
    if (!matchingEvent) {
      setPendingReferenceData(null);
      return;
    }

    if (isTripsExperienceEnabled) {
      const matchingTrip = findContainingReferenceTrip(resolvedTripSummaries, matchingEvent);
      if (!matchingTrip) {
        setPendingReferenceData(null);
        return;
      }

      skipNextSelectionResetRef.current = true;
      setTripDisplayMode('trips');
      setSelectedThingId(matchingTrip.entityId);
      setSelectedTripId(matchingTrip.id);
    } else {
      setTripDisplayMode('events');
      setSelectedThingId(matchingEvent.thingId);
    }

    selectTripEvent(matchingEvent);
    setPendingReferenceData(null);
  }, [
    isTripsCoreExperienceEnabled,
    isTripsExperienceEnabled,
    pendingReferenceData,
    resolvedThingRangeEvents,
    resolvedTripSummaries,
    selectTripEvent,
    thingRangeEventsState,
  ]);

  const selectedTripRouteLabel = useMemo(() => {
    if (!selectedTrip) return '-- - --';
    return tripRouteLabelByTripId[selectedTrip.id] ?? (selectedTrip.isOngoing ? inProgressLabel : singleRouteLabel);
  }, [inProgressLabel, selectedTrip, singleRouteLabel, tripRouteLabelByTripId]);

  const isTimelineEventVisible = useCallback((event: EventLocationData): boolean => {
    const exactMatch = eventDefinitions.find((item) => item.featureId === event.featureTypeId && item.eventTypeId === event.eventTypeId);
    const featureWildcardMatch = eventDefinitions.find((item) => item.featureId === '*' && item.eventTypeId === event.eventTypeId);
    const eventTypeWildcardMatch = eventDefinitions.find((item) => item.featureId === event.featureTypeId && item.eventTypeId === '*');
    const defaultMatch = eventDefinitions.find((item) => item.featureId === '*' && item.eventTypeId === '*');
    const definition = exactMatch ?? featureWildcardMatch ?? eventTypeWildcardMatch ?? defaultMatch;
    if (!definition) return true;
    return definition.enabled && definition.notableEvent;
  }, [eventDefinitions]);

  useEffect(() => {
    setThingRangeSuburbLabelByEventId((current) => (Object.keys(current).length ? {} : current));
    setThingRangeSuburbLabelLoadingByEventId((current) => (Object.keys(current).length ? {} : current));
    setVisibleThingRangeTimelineItemIds((current) => (current.length ? [] : current));
  }, [selectedEntityId, selectedThingDateRangeEndMillis, selectedThingDateRangeStartMillis, tripDisplayMode]);

  useEffect(() => {
    if (reverseGeocodeEnabled) return;
    setThingRangeSuburbLabelByEventId((current) => (Object.keys(current).length ? {} : current));
    setThingRangeSuburbLabelLoadingByEventId((current) => (Object.keys(current).length ? {} : current));
  }, [reverseGeocodeEnabled]);

  const visibleThingRangeAddressEvents = useMemo(() => {
    if (tripDisplayMode !== 'events') return [];

    const visibleItemIds = new Set(visibleThingRangeTimelineItemIds);
    const visibleThingRangeEvents = filterExcludedTripHistoryEvents(resolvedThingRangeEvents)
      .filter((event) => isTimelineEventVisible(event))
      .filter((event) => showThingLocationUpdates || !isLocationUpdateEvent(event));
    const processedTimelineEvents = getTripTimelineProcessedEvents(visibleThingRangeEvents, tripEventSummarizationOptions);

    return processedTimelineEvents
      .filter((entry) => !(entry.isSummarized && entry.eventCount && entry.eventCount > 1))
      .map((entry) => entry.event)
      .filter((event) => visibleItemIds.has(event.eventId));
  }, [
    isTimelineEventVisible,
    resolvedThingRangeEvents,
    showThingLocationUpdates,
    tripDisplayMode,
    tripEventSummarizationOptions,
    visibleThingRangeTimelineItemIds,
  ]);

  useEffect(() => {
    if (!reverseGeocodeEnabled || !visibleThingRangeAddressEvents.length) {
      setThingRangeSuburbLabelLoadingByEventId((current) => (Object.keys(current).length ? {} : current));
      return;
    }

    const missingEvents = visibleThingRangeAddressEvents.filter((event) => !thingRangeSuburbLabelByEventId[event.eventId]);
    if (!missingEvents.length) return;

    let isCancelled = false;
    const reverseGeocodeTimeoutMs = 8000;
    const missingEventIds = missingEvents.map((event) => event.eventId);

    const withTimeout = async <T,>(promise: Promise<T>): Promise<T> => {
      let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
      try {
        return await Promise.race([
          promise,
          new Promise<T>((_, reject) => {
            timeoutId = globalThis.setTimeout(() => {
              reject(new Error('Reverse geocode timed out'));
            }, reverseGeocodeTimeoutMs);
          }),
        ]);
      } finally {
        if (timeoutId !== undefined) {
          globalThis.clearTimeout(timeoutId);
        }
      }
    };

    setThingRangeSuburbLabelLoadingByEventId((current) => {
      const next = { ...current };
      missingEvents.forEach((event) => {
        next[event.eventId] = true;
      });
      return next;
    });

    missingEvents.forEach((event) => {
      void (async () => {
        try {
          const addressLabel = await withTimeout(resolveTimelineAddressText({
            cache: reverseGeocodeLabelByCoordinateRef.current,
            enabled: reverseGeocodeEnabled,
            event,
            inFlight: reverseGeocodeInFlightByCoordinateRef.current,
            reverseGeocode: throttledReverseGeocode,
            unknownLabel: unknownLocationLabel,
          }));
          if (isCancelled) return;
          setThingRangeSuburbLabelByEventId((current) => ({
            ...current,
            [event.eventId]: toLocalityLabel(addressLabel),
          }));
        } catch {
          if (isCancelled) return;
          setThingRangeSuburbLabelByEventId((current) => ({
            ...current,
            [event.eventId]: toLocalityLabel(formatAddressText(event, unknownLocationLabel)),
          }));
        } finally {
          if (!isCancelled) {
            setThingRangeSuburbLabelLoadingByEventId((current) => {
              const next = { ...current };
              delete next[event.eventId];
              return next;
            });
          }
        }
      })();
    });

    return () => {
      isCancelled = true;
      setThingRangeSuburbLabelLoadingByEventId((current) => {
        const next = { ...current };
        missingEventIds.forEach((eventId) => {
          delete next[eventId];
        });
        return next;
      });
    };
  }, [
    reverseGeocodeEnabled,
    thingRangeSuburbLabelByEventId,
    throttledReverseGeocode,
    unknownLocationLabel,
    visibleThingRangeAddressEvents,
  ]);

  const isThingRangeSuburbLabelLoading = useCallback((eventId: string): boolean => {
    return thingRangeSuburbLabelLoadingByEventId[eventId] === true;
  }, [thingRangeSuburbLabelLoadingByEventId]);

  const thingRangeTimelineItems = useMemo<ThingRangeTimelineItem[]>(() => {
    const visibleThingRangeEvents: EventLocationData[] = filterExcludedTripHistoryEvents(resolvedThingRangeEvents)
      .filter((event) => isTimelineEventVisible(event));
    const visibleEvents = visibleThingRangeEvents
      .filter((event) => showThingLocationUpdates || !isLocationUpdateEvent(event));

    const processedTimelineEvents = getTripTimelineProcessedEvents(visibleEvents, tripEventSummarizationOptions);
    const eventItems = processedTimelineEvents.map((entry): ThingRangeTimelineItem => {
      const event = entry.event;
      const originalEvents = entry.originalEvents ?? [];
      const sortedOriginalEvents = [...originalEvents]
        .sort((first, second) => toEventTime(first.occurred).toMillis() - toEventTime(second.occurred).toMillis());
      const firstOccurred = sortedOriginalEvents[0];
      const lastOccurred = sortedOriginalEvents[sortedOriginalEvents.length - 1];
      const isSummarized = entry.isSummarized && entry.eventCount && entry.eventCount > 1;
      const timeLabel = isSummarized && firstOccurred && lastOccurred
        ? `${toEventTime(firstOccurred.occurred).toFormat('hh:mm a')} - ${toEventTime(lastOccurred.occurred).toFormat('hh:mm a')}`
        : toEventTime(event.occurred).toFormat('hh:mm a');

      return {
        color: getEventColor(event, eventDefinitions),
        event: isSummarized
          ? {
            ...event,
            _eventCount: entry.eventCount,
            _isSummarized: true,
            _originalEvents: originalEvents,
            _summarizationMode: entry.summarizationMode,
          } as EventLocationData
          : event,
        id: event.eventId,
        label: getEventDisplayLabel(event, eventDefinitions),
        opensDrawer: true,
        subtitle: isSummarized
          ? `${entry.eventCount ?? 0} Occurrences`
          : (
            isThingRangeSuburbLabelLoading(event.eventId)
              ? <Skeleton data-testid="history-thing-events-subtitle-skeleton" height={16} variant="rounded" width={120} />
              : resolvedThingRangeSuburbLabelByEventId[event.eventId] ?? formatAddressText(event, unknownLocationLabel)
          ),
        time: timeLabel,
        type: 'event',
      };
    });

    const timelineItems = [...eventItems].sort((first, second) => toEventTime(first.event.occurred).toMillis() - toEventTime(second.event.occurred).toMillis());
    return sortOrder === 'oldest-first' ? timelineItems : timelineItems.reverse();
  }, [
    eventDefinitions,
    resolvedThingRangeEvents,
    showThingLocationUpdates,
    sortOrder,
    resolvedThingRangeSuburbLabelByEventId,
    isThingRangeSuburbLabelLoading,
    isTimelineEventVisible,
    tripEventSummarizationOptions,
    unknownLocationLabel,
  ]);

  const selectThingRangeBoundary = useCallback((event: EventLocationData): void => {
    setSelectedTripEventId(null);
    setSelectedEvent(null);
    setTimelineHoveredEventId(null);
    setThingRangeFocusEvent(event);
  }, []);

  useEffect(() => {
    if (tripDisplayMode !== 'events') return;
    if (showThingLocationUpdates || !selectedTripEventId || !selectedEvent || !isLocationUpdateEvent(selectedEvent)) return;
    setSelectedTripEventId(null);
    setSelectedEvent(null);
    setTimelineHoveredEventId(null);
    setThingRangeFocusEvent(null);
  }, [selectedEvent, selectedTripEventId, showThingLocationUpdates, tripDisplayMode]);

  useEffect(() => {
    const entities = tripHistoryData?.entities.filter((entity) => (
      entity.type === CacheDataTypes.THING
      && entity._id !== 'Unknown thing'
      && (!thingDisplayById[entity._id] || thingDisplayById[entity._id] === entity._id)
    )) ?? [];
    if (!entities.length) return;

    let isCancelled = false;

    void Promise.all(entities.map(async (entity) => {
      const thing = await thingCache.one<Thing>(entity._id);
      return thing && !thing.deleted ? { id: entity._id, display: thing.display } : undefined;
    })).then((results) => {
      if (isCancelled) return;
      const displays = results.filter((result): result is { display: string; id: string } => Boolean(result?.display));
      if (!displays.length) return;

      setThingDisplayById((current) => {
        const next = { ...current };
        let hasChanges = false;
        displays.forEach(({ display, id }) => {
          if (next[id] === display) return;
          next[id] = display;
          hasChanges = true;
        });
        return hasChanges ? next : current;
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [thingCache, thingDisplayById, tripHistoryData]);

  const handleAddThing = useCallback((item: CommonEntityWithDeleted, entityItemFields: EntityItemFields): void => {
    const entityId = item._id;
    const today = DateTime.now().startOf('day');
    const todayEnd = today.endOf('day');
    const addedEntity: TripHistoryEntity = {
      _id: entityId,
      type: entityItemFields.entity,
      journeys: [{
        finish: todayEnd.toISO() ?? todayEnd.toJSDate().toISOString(),
        start: today.toISO() ?? today.toJSDate().toISOString(),
      }],
    };

    setTripHistoryData((current) => {
      const currentEntities = current?.entities ?? [];
      const hasEntity = currentEntities.some((entity) => entity._id === entityId);
      const nextEntities = hasEntity
        ? currentEntities.map((entity) => entity._id === entityId ? addedEntity : entity)
        : [...currentEntities, addedEntity];
      setThingColorById((colors) => (
        getThingColorMapTyped as <T extends { _id: string }>(
          entities: T[],
          currentColorById?: Record<string, string>,
          thingColors?: string[],
        ) => Record<string, string>
      )(nextEntities, colors, thingColorPalette));
      return current ? { ...current, entities: nextEntities } : { entities: nextEntities };
    });
    setThingRangesById((current) => ({ ...current, [entityId]: 'today' }));
    setThingCustomRangesById((current) => {
      const next = { ...current };
      delete next[entityId];
      return next;
    });
    setThingCustomRangeDraftsById((current) => {
      const next = { ...current };
      delete next[entityId];
      return next;
    });
    setThingCustomRangeEditorOpenById((current) => {
      const next = { ...current };
      delete next[entityId];
      return next;
    });
    setThingPreviousPresetRangeById((current) => {
      const next = { ...current };
      delete next[entityId];
      return next;
    });
    setThingDisplayById((current) => ({ ...current, [entityId]: item.display || current[entityId] || entityId }));
    setHiddenThingIds((current) => {
      const next = { ...current };
      delete next[entityId];
      return next;
    });
    setRemovedThingIds((current) => {
      const next = { ...current };
      delete next[entityId];
      return next;
    });
    setCollapsedThingIds((current) => ({ ...current, [entityId]: false }));
    setSelectedThingId(entityId);
    setSelectedTripId(null);
    setSelectedEvent(null);
    setSelectedTripEventId(null);
    setIsAddEntityDialogOpen(false);
  }, [thingColorPalette]);

  const renderHistoryContent = (_mobileTabSelection: MobileTabSelection): JSX.Element => {
    const isTripEventDetailsLoading = Object.values(tripEventDetailsLoadingByTripId).some(Boolean);
    const isThingRangeEventsLoading = tripDisplayMode === 'events'
      && selectedEntityId !== 'Unknown thing'
      && selectedThingDateRangeStartMillis !== null
      && selectedThingDateRangeEndMillis !== null
      && thingRangeEventsState === 'pending';
    const isHistoryControlsDisabled = tripSummariesState === 'pending'
      || isTripEventDetailsLoading
      || isThingRangeEventsLoading;

    const closeDateRangeMenu = (): void => {
      setDateRangeMenuAnchor(null);
      setDateRangeMenuEntityId(null);
    };

    const closeThingMenu = (): void => {
      setThingMenuAnchor(null);
      setThingMenuEntityId(null);
      closeDateRangeMenu();
    };

    const selectTripCard = (tripId: string, entityId: string): void => {
      setSelectedThingId(entityId);
      setSelectedTripId(tripId);
      setTripDisplayMode(isTripsExperienceEnabled ? 'trips' : 'events');
    };

    const setThingRange = (entityId: string, range: ThingRange): void => {
      if (range === 'custom') {
        const now = DateTime.now();
        const currentRange: ThingRange = thingRangesById[entityId] ?? 'this-week';
        const previousPresetRange: Exclude<ThingRange, 'custom'> = currentRange === 'custom'
          ? (thingPreviousPresetRangeById[entityId] ?? 'this-week')
          : currentRange;
        const existingCustomRange: Undefinable<ThingCustomRange> = thingCustomRangesById[entityId];
        const entityTrips = payloadTripSummaries.filter((trip) => trip.entityId === entityId);
        const entityBounds = getTripBounds(entityTrips);
        const fallbackBounds = entityBounds ?? getPresetThingRangeBounds(previousPresetRange, now);
        const draftRange = normalizeThingCustomRange(existingCustomRange ?? {
          end: fallbackBounds.end,
          start: fallbackBounds.start,
        });

        setThingCustomRangeDraftsById((current) => ({
          ...current,
          [entityId]: draftRange,
        }));
        setThingCustomRangeEditorOpenById((current) => ({
          ...current,
          [entityId]: true,
        }));
        setThingPreviousPresetRangeById((current) => ({
          ...current,
          [entityId]: previousPresetRange,
        }));

        setSelectedThingId(entityId);
        closeDateRangeMenu();
        setThingMenuAnchor(null);
        setThingMenuEntityId(null);
        return;
      }

      setThingCustomRangeEditorOpenById((current) => ({
        ...current,
        [entityId]: false,
      }));

      setThingRangesById((current) => ({
        ...current,
        [entityId]: range,
      }));
      setSelectedThingId(entityId);
      closeDateRangeMenu();
      setThingMenuAnchor(null);
      setThingMenuEntityId(null);
    };

    const toggleThingVisibility = (entityId: string): void => {
      setHiddenThingIds((current) => ({
        ...current,
        [entityId]: !current[entityId],
      }));
      setSelectedThingId(entityId);
      closeThingMenu();
    };

    const zoomToThingTrips = (entityId: string): void => {
      const targetTrip = resolvedTripSummaries.find((trip) => trip.entityId === entityId)
        ?? payloadTripSummaries.find((trip) => trip.entityId === entityId);
      if (targetTrip) {
        setSelectedThingId(entityId);
        setSelectedTripId(targetTrip.id);
        setCollapsedThingIds((current) => ({ ...current, [entityId]: false }));
      }
      closeThingMenu();
    };

    const removeThing = (entityId: string): void => {
      setTripHistoryData((current) => {
        if (!current) return current;
        return {
          ...current,
          entities: current.entities.filter((entity) => entity._id !== entityId),
        };
      });
      setRemovedThingIds((current) => {
        const next = { ...current };
        delete next[entityId];
        return next;
      });
      setThingCustomRangesById((current) => {
        const next = { ...current };
        delete next[entityId];
        return next;
      });
      setThingCustomRangeDraftsById((current) => {
        const next = { ...current };
        delete next[entityId];
        return next;
      });
      setThingCustomRangeEditorOpenById((current) => {
        const next = { ...current };
        delete next[entityId];
        return next;
      });
      setThingPreviousPresetRangeById((current) => {
        const next = { ...current };
        delete next[entityId];
        return next;
      });
      setThingRangesById((current) => {
        const next = { ...current };
        delete next[entityId];
        return next;
      });
      setHiddenThingIds((current) => {
        const next = { ...current };
        delete next[entityId];
        return next;
      });
      setSelectedThingId((current) => {
        if (current !== entityId) return current;
        return filteredEntities.find((entity) => entity._id !== entityId)?._id ?? null;
      });
      closeThingMenu();
    };

    const viewThingDetail = (entityId: string): void => {
      navigate(`/${thing}/${entityId}`);
      closeThingMenu();
    };

    const renderAddThingButton = (): JSX.Element => (
      <Button
        data-testid="history-add-button"
        disabled={isHistoryControlsDisabled}
        onClick={() => setIsAddEntityDialogOpen(true)}
        style={{ width: '100%' }}
        variant="outlined"
      >
        {t('common:common.action.add-entity', { entity: t('terms:thing', { count: T_ONE }) })}
      </Button>
    );

    const renderAddThingDialog = (): JSX.Element => (
      <AddThingPersonDialog
        allowedTabs={[ThingPersonDialogTabs.THINGS]}
        closeDialog={() => setIsAddEntityDialogOpen(false)}
        data-testid="history-add-dialog"
        onAddEntity={handleAddThing}
        open={isAddEntityDialogOpen}
        operationType={ThingPersonDialogOperation.ADD}
      />
    );

    const renderTripList = (entityId: string, groups: TripGroup[]): JSX.Element => (
      <>
        {tripSummariesState === 'pending' && (
          <TripListSkeleton />
        )}

        {tripSummariesState !== 'pending' && !groups.length && (
          <Typography color="text.secondary" variant="body2">{t('track:page.history.trip-base-view.messages.no-trips-found')}</Typography>
        )}

        {tripSummariesState !== 'pending' && <Stack style={{ maxHeight: 520, overflowY: 'auto', paddingRight: 4 }}>
          <Stack spacing={1.5}>
            {groups.map((group) => (
              <Stack key={`${entityId}-${group.key}`} spacing={1}>
                <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
                  <Stack alignItems="center" direction="row" spacing={1}>
                    <IconButton
                      aria-label={(expandedGroupKeys[`${entityId}:${group.key}`] ?? true)
                        ? t('track:page.history.trip-base-view.aria.collapse-date-group')
                        : t('track:page.history.trip-base-view.aria.expand-date-group')}
                      onClick={() => {
                        setExpandedGroupKeys((current) => ({
                          ...current,
                          [`${entityId}:${group.key}`]: !(current[`${entityId}:${group.key}`] ?? true),
                        }));
                      }}
                      size="small"
                    >
                      <Icon icon={(expandedGroupKeys[`${entityId}:${group.key}`] ?? true) ? 'ChevronUp' : 'ChevronDown'} />
                    </IconButton>
                    <Typography color="text.secondary" style={{ fontWeight: 600 }} variant="body2">{formatTripGroupDayLabel(group.day, t)}</Typography>
                  </Stack>
                  <Typography color="text.secondary" variant="body2">{t('track:page.history.trip-base-view.labels.trips-count', { count: group.trips.length })}</Typography>
                </Stack>
                {(expandedGroupKeys[`${entityId}:${group.key}`] ?? true) && group.trips.map((trip) => {
                  const isSelected = trip.id === selectedTripId;
                  const isDimmed = Boolean(selectedTripId && selectedTripId !== trip.id);
                  const isInProgress = trip.isOngoing;
                  const routeLabel = tripRouteLabelByTripId[trip.id] ?? (isInProgress ? inProgressLabel : singleRouteLabel);
                  const routeFullAddressLabel = tripRouteFullAddressLabelByTripId[trip.id] ?? (isInProgress ? tripInProgressLabel : singleRouteLabel);
                  const geofenceSummary = tripGeofenceSummaryByTripId[trip.id] ?? { items: [] };
                  const [startRouteLabel, endRouteLabel] = routeLabel.includes(' -> ') ? routeLabel.split(' -> ', 2) : [routeLabel, isInProgress ? inProgressLabel : unknownLocationLabel];
                  const [startRouteFullAddress, endRouteFullAddress] = routeFullAddressLabel.includes(' - ') ? routeFullAddressLabel.split(' - ', 2) : [routeFullAddressLabel, isInProgress ? tripInProgressLabel : unknownLocationLabel];
                  const eventSummary = tripEventSummaryByTripId[trip.id] ?? [];
                  const tripMetrics = tripMetricsByTripId[trip.id];
                  const eventCount = eventSummary.reduce((sum, item) => sum + item.count, 0);
                  const highestFaultSeverity = eventSummary.reduce<'High' | 'Medium' | 'Low' | undefined>((highest, item) => {
                    if (!item.isFault || !item.highestFaultSeverity) return highest;
                    const severityRank = { High: 3, Low: 1, Medium: 2 };
                    return !highest || severityRank[item.highestFaultSeverity] > severityRank[highest]
                      ? item.highestFaultSeverity
                      : highest;
                  }, undefined);
                  const isTripDetailsLoading = tripEventDetailsLoadingByTripId[trip.id] === true;

                  // Compute conditional chip arrays for template
                  const hasDistance = tripMetricsByTripId[trip.id]?.distanceKm;
                  const hasFuel = tripMetrics?.fuelLiters;
                  const hasGeofences = Array.isArray(geofenceSummary?.items) && geofenceSummary.items.length > 0;
                  const hasEvents = eventCount > 0;

                  // Base context needed for chip content evaluation (no `items` yet)
                  const baseChipContext = {
                    trip: {
                      entityId: trip.entityId,
                      finish: trip.finish.toISO(),
                      id: trip.id,
                      isOngoing: trip.isOngoing,
                      journeyId: trip.journeyId,
                      start: trip.start.toISO(),
                    },
                    events: {
                      distancePrimaryIncrease: hasDistance ? {
                        data: { increase: hasDistance * 1000 },
                      } : undefined,
                      fuelConsumed: hasFuel ? {
                        data: { increase: hasFuel * 1000 },
                      } : undefined,
                    },
                    geofences: geofenceSummary,
                    eventSummary,
                    ui: {
                      isLoading: isTripDetailsLoading,
                      eventCount,
                    },
                  };

                  // Evaluate each chip's content using evaluate() so the result is a React element
                  const chipItems = [
                    {
                      id: 'duration',
                      content: evaluate(
                        isTripDetailsLoading
                          ? { op: 'ui', name: 'Skeleton', height: 32, variant: 'rounded', width: 80 }
                          : {
                            op: 'ui',
                            name: 'SummaryChip',
                            icon: { op: 'ui', name: 'Icon', fontSize: 'small', icon: 'Clock' },
                            label: { op: 'format-duration', start: { op: 'context', path: 'trip.start' }, finish: { op: 'context', path: 'trip.finish' }, display: 'narrow', shiftTo: ['hours', 'minutes'] },
                            details: { op: 'ui', name: 'Typography', variant: 'body2', children: { op: 'translate', translation: 'track:page.history.trip-base-view.labels.duration' } },
                          },
                        baseChipContext,
                      ),
                    },
                    ...(isTripDetailsLoading || hasDistance ? [{
                      id: 'distance',
                      content: evaluate(
                        isTripDetailsLoading
                          ? { op: 'ui', name: 'Skeleton', height: 32, variant: 'rounded', width: 94 }
                          : {
                            op: 'ui',
                            name: 'SummaryChip',
                            icon: { op: 'ui', name: 'Icon', fontSize: 'small', icon: 'Trip' },
                            label: { op: 'format-distance', meters: { op: 'context', path: 'events.distancePrimaryIncrease.data.increase' }, unit: 'kilometer', display: 'narrow' },
                            details: { op: 'ui', name: 'Typography', variant: 'body2', children: { op: 'translate', translation: 'track:page.history.trip-base-view.labels.total-distance' } },
                          },
                        baseChipContext,
                      ),
                    }] : []),
                    ...(isTripDetailsLoading || hasFuel ? [{
                      id: 'fuel',
                      content: evaluate(
                        isTripDetailsLoading
                          ? { op: 'ui', name: 'Skeleton', height: 32, variant: 'rounded', width: 82 }
                          : {
                            op: 'ui',
                            name: 'SummaryChip',
                            icon: { op: 'ui', name: 'Icon', fontSize: 'small', icon: 'Fuel' },
                            label: { op: 'format-number', value: { op: 'divide', a: { op: 'context', path: 'events.fuelConsumed.data.increase' }, b: 1000 }, format: 'unit', unit: 'liter', fractionDigits: 1 },
                            details: { op: 'ui', name: 'Typography', variant: 'body2', children: { op: 'translate', translation: 'track:page.history.trip-base-view.labels.fuel-consumed' } },
                          },
                        baseChipContext,
                      ),
                    }] : []),
                    ...(hasGeofences ? [{
                      id: 'geofence',
                      content: evaluate(
                        {
                          op: 'ui',
                          name: 'SummaryChip',
                          color: 'success',
                          icon: { op: 'ui', name: 'Icon', fontSize: 'small', icon: 'GeofenceIcon' },
                          label: { op: 'translate', translation: 'track:page.history.trip-base-view.labels.geofence' },
                          placement: 'right',
                          details: {
                            op: 'ui',
                            name: 'Stack',
                            spacing: 1.25,
                            style: { minWidth: 280 },
                            children: {
                              op: 'array:map',
                              items: geofenceSummary?.items ?? [],
                              to: {
                                op: 'ui',
                                name: 'Stack',
                                alignItems: 'center',
                                direction: 'row',
                                style: { alignItems: 'center', columnGap: 8, display: 'grid', gridTemplateColumns: '72px minmax(0, 1fr) 64px' },
                                children: [
                                  {
                                    op: 'ui',
                                    name: 'Stack',
                                    alignItems: 'center',
                                    direction: 'row',
                                    spacing: 1.5,
                                    children: [
                                      {
                                        op: 'ui',
                                        name: 'Icon',
                                        icon: {
                                          op: 'if',
                                          condition: { op: 'context', path: 'item.actionLabel' },
                                          then: { op: 'if', condition: { op: 'eq', a: { op: 'context', path: 'item.actionLabel' }, b: 'Enter' }, then: 'ArrowRightCircle', else: 'ArrowLeftCircle' },
                                        },
                                      },
                                      {
                                        op: 'ui',
                                        name: 'Typography',
                                        variant: 'caption',
                                        style: { whiteSpace: 'nowrap' },
                                        children: { op: 'if', condition: { op: 'eq', a: { op: 'context', path: 'item.actionLabel' }, b: 'Enter' }, then: { op: 'translate', translation: 'track:page.history.trip-base-view.labels.geofence-enter' }, else: { op: 'translate', translation: 'track:page.history.trip-base-view.labels.geofence-exit' } },
                                      },
                                    ],
                                  },
                                  { op: 'ui', name: 'Typography', variant: 'caption', title: { op: 'context', path: 'item.label' }, style: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, children: { op: 'context', path: 'item.label' } },
                                  { op: 'ui', name: 'Typography', color: 'text.secondary', variant: 'caption', style: { paddingRight: 16, textAlign: 'right', whiteSpace: 'nowrap' }, children: { op: 'context', path: 'item.occurredLabel' } },
                                ],
                              },
                            },
                          },
                        },
                        baseChipContext,
                      ),
                    }] : []),
                    ...(hasEvents ? [{
                      id: 'events',
                      content: evaluate(
                        isTripDetailsLoading
                          ? { op: 'ui', name: 'Skeleton', height: 32, variant: 'rounded', width: 88 }
                          : {
                            op: 'ui',
                            name: 'SummaryChip',
                            color: 'info',
                            icon: { op: 'ui', name: 'Icon', fontSize: 'small', icon: 'Info' },
                            label: { op: 'translate', translation: 'track:page.history.trip-base-view.labels.events-count', count: { op: 'context', path: 'ui.eventCount' } },
                            placement: 'right',
                            details: {
                              op: 'ui',
                              name: 'Stack',
                              spacing: 1.25,
                              style: { minWidth: 220 },
                              children: {
                                op: 'array:map',
                                items: eventSummary,
                                to: {
                                  op: 'ui',
                                  name: 'Stack',
                                  alignItems: 'center',
                                  direction: 'row',
                                  justifyContent: 'space-between',
                                  spacing: 2,
                                  children: [
                                    {
                                      op: 'ui',
                                      name: 'Stack',
                                      alignItems: 'center',
                                      direction: 'row',
                                      spacing: 1.5,
                                      children: [
                                        { op: 'ui', name: 'Box', bgcolor: { op: 'context', path: 'item.color' }, style: { borderRadius: '50%', height: 12, width: 12 } },
                                        { op: 'ui', name: 'Typography', variant: 'body2', children: { op: 'context', path: 'item.label' } },
                                      ],
                                    },
                                    { op: 'ui', name: 'Typography', color: 'text.secondary', variant: 'body2', children: { op: 'context', path: 'item.count' } },
                                  ],
                                },
                              },
                            },
                          },
                        baseChipContext,
                      ),
                    }] : []),
                  ];

                  const tripSummaryCardContext = {
                    trip: {
                      entityId: trip.entityId,
                      finish: trip.finish.toISO(),
                      id: trip.id,
                      isOngoing: trip.isOngoing,
                      journeyId: trip.journeyId,
                      start: trip.start.toISO(),
                    },
                    metrics: {
                      distanceKm: tripMetrics?.distanceKm,
                      fuelLiters: tripMetrics?.fuelLiters,
                      engineHoursEndMinutes: tripMetrics?.engineHoursEndMinutes,
                      engineHoursStartMinutes: tripMetrics?.engineHoursStartMinutes,
                      odometerEndMeters: tripMetrics?.odometerEndMeters,
                      odometerStartMeters: tripMetrics?.odometerStartMeters,
                    },
                    events: {
                      distancePrimaryIncrease: hasDistance ? {
                        data: { increase: hasDistance * 1000 },
                      } : undefined,
                      fuelConsumed: hasFuel ? {
                        data: { increase: hasFuel * 1000 },
                      } : undefined,
                    },
                    geofences: geofenceSummary,
                    eventSummary,
                    faultSummary: { highestSeverity: highestFaultSeverity },
                    // Pre-evaluated chip items - content is already a React element
                    items: chipItems,
                    ui: {
                      isLoading: isTripDetailsLoading,
                      accentColor: thingColorById[trip.entityId] ?? selectedThingColor,
                      routeLabel: {
                        start: startRouteLabel,
                        end: endRouteLabel,
                      },
                      routeFullAddress: {
                        start: startRouteFullAddress,
                        end: endRouteFullAddress,
                      },
                      startTime: trip.start.toFormat('hh:mm a'),
                      endTime: isInProgress ? t('track:page.history.trip-base-view.labels.current') : trip.finish.toFormat('hh:mm a'),
                      startDateTime: trip.start.toFormat('dd LLL yyyy, hh:mm a'),
                      endDateTime: isInProgress ? t('track:page.history.trip-base-view.labels.current') : trip.finish.toFormat('dd LLL yyyy, hh:mm a'),
                      eventCount,
                      selected: isSelected,
                    },
                  };

                  return (
                    <Box
                      key={trip.id}
                      aria-pressed={isSelected}
                      data-testid="trip-card"
                      onClick={() => selectTripCard(trip.id, entityId)}
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        selectTripCard(trip.id, entityId);
                      }}
                      role="button"
                      style={{
                        cursor: 'pointer',
                        opacity: isDimmed ? 0.75 : 1,
                        paddingLeft: 2,
                        paddingRight: 2,
                        transition: 'opacity 120ms ease',
                      }}
                      tabIndex={0}
                    >
                      {tripSummaryCardTemplateLoaded ? <>{evaluate(tripSummaryCardTemplate, tripSummaryCardContext)}</> : <RecordSummaryCard accentColor={thingColorById[trip.entityId] ?? selectedThingColor} clickable selected={isSelected} subtitle={<Stack alignItems="center" direction="row" spacing={0.5}><Skeleton height={18} variant="text" width={180} /></Stack>} title={<Skeleton height={24} variant="text" width={120} />} />}
                    </Box>
                  );
                })}
              </Stack>
            ))}
          </Stack>
        </Stack>}
      </>
    );

    const renderThingEventsContent = (entityId: string): JSX.Element => {
      if (entityId !== selectedEntityId) {
        return <Typography color="text.secondary" variant="body2">{t('track:page.history.trip-base-view.messages.select-thing-for-events')}</Typography>;
      }

      return (
        <>
          {thingRangeEventsState === 'pending' && (
            <ThingEventsSkeleton />
          )}
          {thingRangeEventsState !== 'pending' && !thingRangeTimelineItems.length && (
            <Typography color="text.secondary" variant="body2">{t('track:page.history.trip-base-view.messages.no-events-found')}</Typography>
          )}
          {thingRangeEventsState !== 'pending' && thingRangeTimelineItems.length > 0 && (
            <>
              <Button
                color="primary"
                data-testid="history-thing-events-location-updates-button"
                onClick={() => setShowThingLocationUpdates((current) => !current)}
                style={{ alignSelf: 'flex-start' }}
                variant={'light' as 'text'}
              >
                <Stack alignItems="center" direction="row" spacing={1}>
                  <Switch
                    checked={showThingLocationUpdates}
                    data-testid="history-thing-events-location-updates-toggle"
                    onClick={(event) => event.stopPropagation()}
                    onChange={(_event, checked) => setShowThingLocationUpdates(checked)}
                    size="small"
                  />
                  <Typography lineHeight="2.5" variant="body2">{t('track:page.history.trip-base-view.labels.location-updates')}</Typography>
                </Stack>
              </Button>

              <Box style={{ maxHeight: 520, overflowY: 'auto', paddingRight: 4 }}>
                <ThingRangeTimeline
                  hoveredEventId={timelineHoveredEventId}
                  itemTemplate={thingRangeTimelineItemTemplate}
                  itemTemplateLoaded={thingRangeTimelineItemTemplateLoaded}
                  items={thingRangeTimelineItems}
                  markerTemplate={thingRangeTimelineMarkerTemplate}
                  markerTemplateLoaded={thingRangeTimelineMarkerTemplateLoaded}
                  onHoverEvent={setTimelineHoveredEventId}
                  onSelectBoundary={selectThingRangeBoundary}
                  onSelectEvent={(event: EventLocationData) => {
                    setThingRangeFocusEvent(event);
                    selectTripEvent(event);
                  }}
                  onVisibleItemIdsChange={setVisibleThingRangeTimelineItemIds}
                  selectedEventId={selectedTripEventId}
                />
              </Box>
            </>
          )}
        </>
      );
    };

    const renderThingCard = (entity: TripHistoryEntity): JSX.Element => {
      const entityId = entity._id;
      const entityTrips = resolvedTripSummaries.filter((trip) => trip.entityId === entityId);
      const entityPayloadTrips = payloadTripSummaries.filter((trip) => trip.entityId === entityId);
      const entityGroups = getTripGroups(entityTrips, sortOrder);
      const entityBounds = getTripBounds(entityPayloadTrips);
      const isThingDetailsLoading = entityTrips.some((trip) => tripEventDetailsLoadingByTripId[trip.id] === true);
      const isThingHidden = hiddenThingIds[entityId] === true;
      const isThingCollapsed = collapsedThingIds[entityId] ?? false;
      const isThingHeaderHovered = thingHeaderHoveredById[entityId] === true;
      const shouldShowChevronToggle = mobileLayout || isThingHeaderHovered;
      const headerColor = isThingHidden ? 'text.disabled' : 'text.primary';
      const thingColor = thingColorById[entityId] ?? thingColorPalette[0];
      const thingDisplay = thingDisplayById[entityId] ?? entityId;
      const canViewThingDetail = entity.type === CacheDataTypes.THING && entityId !== 'Unknown thing';
      const selectedRange: ThingRange = thingRangesById[entityId] ?? (thingCustomRangesById[entityId] ? 'custom' : 'this-week');
      const customRange: Undefinable<ThingCustomRange> = thingCustomRangesById[entityId];
      const customRangeDraft: Undefinable<ThingCustomRange> = thingCustomRangeDraftsById[entityId] ?? customRange;
      const isCustomRangeEditorOpen = thingCustomRangeEditorOpenById[entityId] === true;
      const previousPresetRange = thingPreviousPresetRangeById[entityId] ?? 'this-week';
      const now = DateTime.now();
      const hasCustomRangeValue = Boolean(customRangeDraft?.start && customRangeDraft.end);
      const hasFutureDateInCustomRange = Boolean(
        customRangeDraft?.start && customRangeDraft.start.toMillis() > now.toMillis(),
      ) || Boolean(
        customRangeDraft?.end && customRangeDraft.end.toMillis() > now.toMillis(),
      );
      const hasInvalidCustomRangeOrder = Boolean(
        customRangeDraft?.start
        && customRangeDraft?.end
        && customRangeDraft.start.toMillis() > customRangeDraft.end.toMillis(),
      );
      const maxHistoryCustomDateRangeDays = Math.ceil(maxHistoryCustomDateRangeHours / 24);
      const maxHistoryCustomDateRangeLabel = toMaxRangeDaysLabel(maxHistoryCustomDateRangeHours);
      const hasExceededCustomRangeLimit = isTripsCoreExperienceEnabled && isRangeOverMaxHours(
        customRangeDraft?.start ?? undefined,
        customRangeDraft?.end ?? undefined,
        maxHistoryCustomDateRangeHours,
      );
      const hasCustomRangeError = hasFutureDateInCustomRange || hasInvalidCustomRangeOrder || hasExceededCustomRangeLimit;
      const canSetCustomRange = hasCustomRangeValue && !hasCustomRangeError;
      const startMaxDateTime = customRangeDraft?.end && customRangeDraft.end.toMillis() < now.toMillis()
        ? customRangeDraft.end
        : now;
      const selectedRangeBounds = toThingRangeBounds(selectedRange, customRange, DateTime.now());
      const thingHeaderBounds = selectedRangeBounds ?? entityBounds;

      return (
        <Card
          key={entityId}
          onMouseEnter={() => setThingHeaderHoveredById((current) => ({ ...current, [entityId]: true }))}
          onMouseLeave={() => setThingHeaderHoveredById((current) => ({ ...current, [entityId]: false }))}
          style={{ borderLeft: `8px solid ${thingColor}` }}
          variant="outlined"
        >
          <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1} style={{ padding: '12px 16px 0 16px' }}>
            <Stack alignItems="center" direction="row" spacing={1}>
              <IconButton
                aria-label={isThingCollapsed ? 'Expand thing card' : 'Collapse thing card'}
                data-testid="history-thing-card-toggle"
                onClick={() => {
                  setSelectedThingId(entityId);
                  setCollapsedThingIds((current) => ({ ...current, [entityId]: !isThingCollapsed }));
                }}
                size="small"
                style={{ color: isThingHidden ? 'rgba(0, 0, 0, 0.38)' : undefined }}
              >
                {shouldShowChevronToggle ? <Icon icon={isThingCollapsed ? 'ChevronDown' : 'ChevronUp'} /> : <Icon icon="ThingIcon" />}
              </IconButton>
              <Stack data-testid="history-thing-header-text" onClick={() => setSelectedThingId(entityId)} spacing={0.25} style={{ cursor: 'pointer' }}>
                <Typography color={headerColor} style={{ fontSize: '1rem', fontWeight: 600 }} title={thingDisplay}>{thingDisplay}</Typography>
                <Typography color="text.secondary" variant="caption">
                  {thingHeaderBounds
                    ? `${thingHeaderBounds.start.toFormat('dd LLL, hh:mm a')} - ${thingHeaderBounds.end.toFormat('dd LLL, hh:mm a')}`
                    : 'No trip date range'
                  }
                </Typography>
              </Stack>
            </Stack>

            <IconButton
              aria-label="Thing actions"
              data-testid="history-thing-menu-button"
              disabled={isHistoryControlsDisabled}
              onClick={(event) => {
                setSelectedThingId(entityId);
                setThingMenuEntityId(entityId);
                setThingMenuAnchor(event.currentTarget);
              }}
            >
              <Icon icon="DotMenuVertical" />
            </IconButton>
          </Stack>

          <Menu anchorEl={thingMenuAnchor} data-testid="history-thing-menu" onClose={closeThingMenu} open={thingMenuEntityId === entityId && Boolean(thingMenuAnchor) && !isHistoryControlsDisabled} PaperProps={{ style: { minWidth: 220 } }}>
            <MenuItem
              data-testid="history-thing-menu-change-range"
              onClick={(event) => {
                setDateRangeMenuEntityId(entityId);
                setDateRangeMenuAnchor(event.currentTarget);
              }}
            >
              <ListItemIcon><Icon icon="Calendar" /></ListItemIcon>
              <ListItemText>{t('track:page.history.trip-base-view.menu.change-range')}</ListItemText>
              <Icon icon="ChevronRight" />
            </MenuItem>
            <MenuItem data-testid="history-thing-menu-toggle-visibility" onClick={() => toggleThingVisibility(entityId)}>
              <ListItemIcon>{isThingHidden ? <Icon icon="Eye" /> : <Icon icon="EyeOff" />}</ListItemIcon>
              <ListItemText>{isThingHidden ? t('track:page.history.trip-base-view.menu.show') : t('track:page.history.trip-base-view.menu.hide')}</ListItemText>
            </MenuItem>
            <MenuItem data-testid="history-thing-menu-zoom" onClick={() => zoomToThingTrips(entityId)}>
              <ListItemIcon><Icon icon="CrossHair" /></ListItemIcon>
              <ListItemText>{t('track:page.history.trip-base-view.menu.zoom')}</ListItemText>
            </MenuItem>
            {canViewThingDetail && (
              <MenuItem data-testid="history-thing-menu-view-thing-detail" onClick={() => viewThingDetail(entityId)}>
                <ListItemIcon><Icon icon="ThingIcon" /></ListItemIcon>
                <ListItemText>{t('track:page.thing-map.view-thing-detail.action')}</ListItemText>
              </MenuItem>
            )}
            <MenuItem data-testid="history-thing-menu-remove" onClick={() => removeThing(entityId)}>
              <ListItemIcon><Icon icon="Trash" /></ListItemIcon>
              <ListItemText>{t('track:page.history.trip-base-view.menu.remove')}</ListItemText>
            </MenuItem>
          </Menu>

          <Menu
            anchorEl={dateRangeMenuAnchor}
            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
            data-testid="history-thing-date-range-menu"
            onClose={closeDateRangeMenu}
            open={dateRangeMenuEntityId === entityId && Boolean(dateRangeMenuAnchor)}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          >
            <MenuItem data-testid="history-thing-range-today" onClick={() => setThingRange(entityId, 'today')}><ListItemText>{t('common:component.date-time-range.labels.today')}</ListItemText></MenuItem>
            <MenuItem data-testid="history-thing-range-yesterday" onClick={() => setThingRange(entityId, 'yesterday')}><ListItemText>{t('common:component.date-time-range.labels.yesterday')}</ListItemText></MenuItem>
            <MenuItem data-testid="history-thing-range-this-week" onClick={() => setThingRange(entityId, 'this-week')}><ListItemText>{t('track:page.history.trip-base-view.range.this-week')}</ListItemText></MenuItem>
            <MenuItem data-testid="history-thing-range-last-week" onClick={() => setThingRange(entityId, 'last-week')}><ListItemText>{t('track:page.history.trip-base-view.range.last-week')}</ListItemText></MenuItem>
            <MenuItem data-testid="history-thing-range-custom" onClick={() => setThingRange(entityId, 'custom')}><ListItemText>{t('track:page.history.trip-base-view.range.custom')}</ListItemText></MenuItem>
          </Menu>

          <Stack spacing={1} style={{ paddingBottom: 24, paddingLeft: 24, paddingRight: 24, paddingTop: 16 }}>
            {tripSummariesState === 'pending' || isThingDetailsLoading ? (
              <ThingSummarySkeleton />
            ) : thingSummaryStatisticsTemplateLoaded ? (() => {
              let totalDistanceMeters = 0;
              let totalFuelMilliliters = 0;
              let totalDurationMinutes = 0;
              entityGroups.forEach((group) => {
                group.trips.forEach((trip) => {
                  const distanceKm = tripMetricsByTripId[trip.id]?.distanceKm;
                  if (distanceKm !== undefined) totalDistanceMeters += distanceKm * 1000;
                  const fuelLiters = tripMetricsByTripId[trip.id]?.fuelLiters;
                  if (fuelLiters !== undefined) totalFuelMilliliters += fuelLiters * 1000;
                  totalDurationMinutes += Math.max(Math.round(trip.finish.diff(trip.start, 'minutes').minutes), 0);
                });
              });
              const thingSummaryContext = {
                'trip-count': entityTrips.length,
                distance: Math.round(totalDistanceMeters),
                'fuel-consumed': Math.round(totalFuelMilliliters),
                duration: totalDurationMinutes,
                entityId,
              };
              return <>{evaluate(thingSummaryStatisticsTemplate, thingSummaryContext)}</>;
            })() : null}

            {customRangeDraft && isCustomRangeEditorOpen && (
              <Card style={{ marginTop: 8 }} variant="outlined">
                <CardContent>
                  <LocalizationProvider adapterLocale={getPortalLocale()}>
                    <Stack spacing={2} mt={1}>
                      {hasCustomRangeError && (
                        <Alert severity="error">
                          {hasExceededCustomRangeLimit
                            ? t('common:component.date-picker.hint.invalid-range-over', { count: maxHistoryCustomDateRangeDays }).replace(`${maxHistoryCustomDateRangeDays}`, maxHistoryCustomDateRangeLabel)
                            : t('common:component.date-picker.hint.invalid-range')}
                        </Alert>
                      )}
                      <DateTimePicker
                        data-testid="history-thing-custom-range-start"
                        format={getDateTimeFormat()}
                        label="Start Date"
                        maxDateTime={startMaxDateTime}
                        onChange={(value: DateTime | null) => {
                          setThingCustomRangeDraftsById((current) => ({
                            ...current,
                            [entityId]: {
                              end: current[entityId]?.end ?? customRangeDraft.end,
                              start: value,
                            },
                          }));
                        }}
                        size="extra-small"
                        value={customRangeDraft.start}
                      />
                      <DateTimePicker
                        data-testid="history-thing-custom-range-end"
                        format={getDateTimeFormat()}
                        label="End Date"
                        maxDateTime={now}
                        minDateTime={customRangeDraft.start ?? undefined}
                        onChange={(value: DateTime | null) => {
                          setThingCustomRangeDraftsById((current) => ({
                            ...current,
                            [entityId]: {
                              end: value,
                              start: current[entityId]?.start ?? customRangeDraft.start,
                            },
                          }));
                        }}
                        size="extra-small"
                        value={customRangeDraft.end}
                      />
                    </Stack>
                  </LocalizationProvider>
                </CardContent>
                <CardActions style={{ justifyContent: 'flex-end', paddingTop: 0 }}>
                  <Button
                    data-testid="history-thing-custom-range-cancel"
                    onClick={() => {
                      setThingRangesById((current) => ({
                        ...current,
                        [entityId]: previousPresetRange,
                      }));
                      setThingCustomRangesById((current) => {
                        const next = { ...current };
                        delete next[entityId];
                        return next;
                      });
                      setThingCustomRangeDraftsById((current) => {
                        const next = { ...current };
                        delete next[entityId];
                        return next;
                      });
                      setThingCustomRangeEditorOpenById((current) => ({
                        ...current,
                        [entityId]: false,
                      }));
                    }}
                    size="small"
                    variant="text"
                  >
                    {t('common:common.action.cancel')}
                  </Button>
                  <Button
                    data-testid="history-thing-custom-range-set"
                    disabled={!canSetCustomRange}
                    onClick={() => {
                      if (!customRangeDraft.start || !customRangeDraft.end) return;

                      const nextCustomRange = normalizeThingCustomRange(customRangeDraft);
                      setThingCustomRangesById((current) => ({
                        ...current,
                        [entityId]: nextCustomRange,
                      }));
                      setThingRangesById((current) => ({
                        ...current,
                        [entityId]: 'custom',
                      }));
                      setThingCustomRangeDraftsById((current) => {
                        const next = { ...current };
                        delete next[entityId];
                        return next;
                      });
                      setThingCustomRangeEditorOpenById((current) => ({
                        ...current,
                        [entityId]: false,
                      }));
                    }}
                    size="small"
                    variant="contained"
                  >
                    {t('common:component.datetime-range-select.labels.set')}
                  </Button>
                </CardActions>
              </Card>
            )}
          </Stack>

          <Divider />

          <Collapse in={!isThingCollapsed} timeout="auto" unmountOnExit>
            <CardContent style={{ padding: '16px 16px 16px 16px' }}>
              <Stack spacing={1.5}>
                {!isThingHidden ? (
                  <>
                    <Stack alignItems="center" direction="row" justifyContent="space-between">
                      <Button
                        data-testid="history-trip-display-mode-button"
                        disabled={isHistoryControlsDisabled || !isTripsExperienceEnabled}
                        endIcon={<Icon icon="ChevronDown" />}
                        onClick={(event) => {
                          setSelectedThingId(entityId);
                          setViewModeMenuAnchor(event.currentTarget);
                        }}
                        size="small"
                        style={{ letterSpacing: 0.4, textTransform: 'uppercase' }}
                        variant="text"
                      >
                        {tripDisplayMode === 'events'
                          ? t('track:page.history.trip-base-view.labels.by-events')
                          : t('track:page.history.trip-base-view.labels.by-trips')}
                      </Button>
                      <Button
                        data-testid="history-trip-sort-order-button"
                        disabled={isHistoryControlsDisabled}
                        endIcon={<Icon icon="ChevronDown" />}
                        onClick={(event) => {
                          setSelectedThingId(entityId);
                          setSortOrderMenuAnchor(event.currentTarget);
                        }}
                        size="small"
                        style={{ letterSpacing: 0.4, textTransform: 'uppercase' }}
                        variant="text"
                      >
                        {sortOrder === 'latest-first'
                          ? t('track:page.history.trip-base-view.labels.most-recent')
                          : t('track:page.history.trip-base-view.labels.oldest-first')}
                      </Button>
                    </Stack>

                    {tripDisplayMode === 'events' || !isTripsExperienceEnabled
                      ? renderThingEventsContent(entityId)
                      : renderTripList(entityId, entityGroups)
                    }
                  </>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    {t('track:page.history.trip-base-view.messages.hidden-from-map')}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Collapse>
        </Card>
      );
    };

    return (
      <Box
        p={2}
        style={{
          boxSizing: 'border-box',
          height: '100%',
          overflowY: 'auto',
        }}
      >
        {filteredEntities.length ? (
          <Stack spacing={2}>
            {filteredEntities.map(renderThingCard)}
            {renderAddThingButton()}
          </Stack>
        ) : (
          <Stack spacing={2.5} style={{ paddingTop: 32 }}>
            <Typography align="center" color="text.secondary" variant="body2">{t('track:page.history.trip-base-view.messages.add-thing-to-continue')}</Typography>
            {renderAddThingButton()}
          </Stack>
        )}
        <Menu
          anchorEl={viewModeMenuAnchor}
          data-testid="history-trip-display-mode-menu"
          onClose={() => setViewModeMenuAnchor(null)}
          open={Boolean(viewModeMenuAnchor) && !isHistoryControlsDisabled && isTripsExperienceEnabled}
        >
          <MenuItem
            data-testid="history-trip-display-mode-trips"
            onClick={() => {
              setTripDisplayMode('trips');
              setViewModeMenuAnchor(null);
            }}
            selected={tripDisplayMode === 'trips'}
          >
            {t('track:page.history.trip-base-view.labels.by-trips')}
          </MenuItem>
          <MenuItem
            data-testid="history-trip-display-mode-events"
            onClick={() => {
              setTripDisplayMode('events');
              setViewModeMenuAnchor(null);
            }}
            selected={tripDisplayMode === 'events'}
          >
            {t('track:page.history.trip-base-view.labels.by-events')}
          </MenuItem>
        </Menu>
        <Menu
          anchorEl={sortOrderMenuAnchor}
          data-testid="history-trip-sort-order-menu"
          onClose={() => setSortOrderMenuAnchor(null)}
          open={Boolean(sortOrderMenuAnchor) && !isHistoryControlsDisabled}
        >
          <MenuItem
            data-testid="history-trip-sort-order-most-recent"
            onClick={() => {
              setSortOrder('latest-first');
              setSortOrderMenuAnchor(null);
            }}
            selected={sortOrder === 'latest-first'}
          >
            {t('track:page.history.trip-base-view.labels.most-recent')}
          </MenuItem>
          <MenuItem
            data-testid="history-trip-sort-order-oldest"
            onClick={() => {
              setSortOrder('oldest-first');
              setSortOrderMenuAnchor(null);
            }}
            selected={sortOrder === 'oldest-first'}
          >
            {t('track:page.history.trip-base-view.labels.oldest')}
          </MenuItem>
        </Menu>
        {renderAddThingDialog()}
      </Box>
    );
  };

  const renderTripDetailContent = (): JSX.Element => {
    if (!selectedTrip) return <></>;

    return (
      <TripDrawerContent
        eventDefinitions={eventDefinitions}
        hoveredEventId={timelineHoveredEventId}
        onHoverEvent={setTimelineHoveredEventId}
        onSelectEvent={selectTripEvent}
        reverseGeocodeEnabled={reverseGeocodeEnabled}
        selectedEventId={selectedTripEventId}
        selectedTripMetricEvents={selectedTrip ? tripMetricEventsByTripId[selectedTrip.id] : undefined}
        selectedTripMetrics={selectedTrip ? tripMetricsByTripId[selectedTrip.id] : undefined}
        selectedTrip={selectedTrip}
        selectedTripRouteLabel={selectedTripRouteLabel}
        selectedThingColor={selectedThingColor}
        thingColorById={thingColorById}
        tripLocationEnrichmentOptions={tripLocationEnrichmentOptions}
        tripEventSummarizationOptions={tripEventSummarizationOptions}
      />
    );
  };

  const renderTripEventDetailContent = (options?: { disableEventDataScroll?: boolean }): JSX.Element => {
    return (
      <TripEventDrawerContent
        disableEventDataScroll={options?.disableEventDataScroll}
        eventDefinitions={eventDefinitions}
        eventModeEvents={tripDisplayMode === 'events' ? thingRangeTimelineItems.map((item) => item.event) : undefined}
        eventModeShowLocationUpdates={showThingLocationUpdates}
        eventModeSortOrder={sortOrder}
        onClose={() => {
          setSelectedTripEventId(null);
          setSelectedEvent(null);
          setThingRangeFocusEvent(null);
        }}
        onSelectEvent={selectTripEvent}
        selectedEvent={selectedEvent}
        selectedEventId={selectedTripEventId}
        selectedTripMetricEvents={selectedTrip ? tripMetricEventsByTripId[selectedTrip.id] : undefined}
        selectedTrip={selectedTrip}
        tripEventSummarizationOptions={tripEventSummarizationOptions}
      />
    );
  };

  const thingRangeEventsForMap = useMemo(() => {
    return resolvedThingRangeEvents.filter(
      (event) => isLocationUpdateEvent(event) || isTimelineEventVisible(event),
    );
  }, [resolvedThingRangeEvents, isTimelineEventVisible]);

  const renderMapContent = (): JSX.Element => {
    return (
      <EventHistoryMap
        center={HISTORY_MAP_CENTER}
        hoveredEventId={hoveredEventId}
        onSelectEvent={selectTripEvent}
        selectedEvent={selectedEvent}
        setHoveredEventId={setHoveredEventId}
        setSelectedEvent={setSelectedEvent}
        storageKey={MapStorageKeys.HISTORY_MAP_SAVED_POSITION}
        visibleJourneyIds={mapVisibleJourneyIds}
        visibleTripId={tripDisplayMode === 'trips' ? selectedTripId ?? undefined : undefined}
      >
        <ThingRangeMapState
          events={thingRangeEventsForMap}
          isActive={tripDisplayMode === 'events' && !isSelectedThingHidden}
          journeyId={thingRangeJourneyId}
          selectedEntityId={selectedEntityId}
          showLocationUpdates={showThingLocationUpdates}
          thingColor={selectedThingColor}
        />
        {tripDisplayMode === 'events' ? (
          <ThingRangeMapViewportController
            events={thingRangeEventsForMap}
            focusEvent={thingRangeFocusEvent}
            isActive={tripDisplayMode === 'events' && !isSelectedThingHidden}
            journeyId={thingRangeJourneyId}
            selectedEventId={selectedTripEventId}
          />
        ) : (
          <TripHistoryMapViewportController
            selectedEventId={selectedTripEventId}
            selectedTrip={selectedTrip}
          />
        )}
        <HoveredEventPulseOverlay
          eventDefinitions={eventDefinitions}
          hoveredEventId={timelineHoveredEventId}
          visibleJourneyIds={mapVisibleJourneyIds}
          visibleTripId={tripDisplayMode === 'trips' ? selectedTripId ?? undefined : undefined}
        />
        {!mobileLayout && (
          <EventHistoryAlert
            message={<Trans components={[<i key="message-note" />]} i18nKey="track:page.history.no-history-message.labels" />}
            sx={{ p: 2, position: 'absolute', width: '100%', zIndex: 1300 }}
            title={t('track:page.history.no-history.labels')}
          />
        )}
      </EventHistoryMap>
    );
  };

  if (mobileLayout) {
    return (
      <Box data-testid="trip-history-view" height="100%" width="100%" minWidth={0} display="flex" flexDirection="column">
        <NotableEventsProvider>
          <HistorySearchProvider key={providerKey} defaultEntityItems={defaultEntityItems} defaultSelectedEventId={defaultSelectedEventId} defaultVisibilities={defaultVisibilities} syncUrlWithRoute={false}>
            <BatchFaultWrapper>
              <EventDialogProvider setSelectedEvent={setSelectedEvent}>
                <ListSearchProvider dataKey="event-history">
                  <EventHistoryNavigator />
                  <Box height="100%" width="100%" position="relative" overflow="hidden">
                    {renderMapContent()}

                    <Box
                      position="absolute"
                      top={0}
                      right={0}
                      bottom={0}
                      left={0}
                      zIndex={1200}
                      style={{
                        backgroundColor: theme.palette.background.paper,
                        overflowY: 'auto',
                      }}
                    >
                      {renderHistoryContent(MobileTabSelection.LEFT)}
                    </Box>

                    <Drawer
                      anchor="bottom"
                      onClose={() => {
                        setSelectedTripId(null);
                        setSelectedEvent(null);
                        setSelectedTripEventId(null);
                        setThingRangeFocusEvent(null);
                      }}
                      open={Boolean(selectedTrip) && tripDisplayMode === 'trips'}
                      slotProps={{
                        paper: {
                          sx: {
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            height: 'calc(100% - 150px)',
                            maxHeight: 'calc(100% - 150px)',
                            overflow: 'hidden',
                          },
                        },
                      }}
                    >
                      <IconButton
                        aria-label={t('common:common.action.close')}
                        onClick={() => {
                          setSelectedTripId(null);
                          setSelectedEvent(null);
                          setSelectedTripEventId(null);
                          setThingRangeFocusEvent(null);
                        }}
                        size="small"
                        style={{ position: 'absolute', right: 16, top: 16, zIndex: 1 }}
                      >
                        <Icon icon="X" />
                      </IconButton>
                      {renderTripDetailContent()}
                    </Drawer>

                    <Drawer
                      anchor="right"
                      style={{ zIndex: theme.zIndex.drawer + 1 }}
                      onClose={() => {
                        setSelectedTripEventId(null);
                        setSelectedEvent(null);
                        setThingRangeFocusEvent(null);
                      }}
                      open={Boolean(selectedTripEventId)}
                      slotProps={{
                        paper: {
                          sx: {
                            maxWidth: '100%',
                            width: '100%',
                          },
                        },
                      }}
                    >
                      <Stack spacing={2}>
                        <Box height="45vh" maxHeight={450} minHeight={220} width="100%">
                          {renderMapContent()}
                        </Box>
                        <Box p={2} pt={0}>
                          {renderTripEventDetailContent({ disableEventDataScroll: true })}
                        </Box>
                      </Stack>
                    </Drawer>
                  </Box>
                </ListSearchProvider>
              </EventDialogProvider>
            </BatchFaultWrapper>
          </HistorySearchProvider>
        </NotableEventsProvider>
      </Box>
    );
  }

  return (
    <Box data-testid="trip-history-view" height="100%" width="100%" minWidth={0} display="flex" flexDirection="column">
      <NotableEventsProvider>
        <HistorySearchProvider key={providerKey} defaultEntityItems={defaultEntityItems} defaultSelectedEventId={defaultSelectedEventId} defaultVisibilities={defaultVisibilities} syncUrlWithRoute={false}>
          <BatchFaultWrapper>
            <EventDialogProvider setSelectedEvent={setSelectedEvent}>
              <ListSearchProvider dataKey="event-history">
                <EventHistoryNavigator />
                <EventHistory
                  isBottomDrawerOpen={Boolean(selectedTripEventId)}
                  isDrawerOpen={isDrawerOpen}
                  renderBottomDrawerContent={selectedTripEventId ? renderTripEventDetailContent : renderTripDetailContent}
                  renderHistoryContent={renderHistoryContent}
                  renderRightDrawerContent={tripDisplayMode === 'trips' && selectedTrip ? renderTripDetailContent : undefined}
                  renderTopDrawerContent={renderMapContent}
                  setIsTransitioning={setIsTransitioning}
                />
              </ListSearchProvider>
            </EventDialogProvider>
          </BatchFaultWrapper>
        </HistorySearchProvider>
      </NotableEventsProvider>
    </Box>
  );
};
